import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { v4 as uuid } from 'uuid';
import { DndContext, PointerSensor, useSensor, useSensors, useDroppable, closestCenter, } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy, horizontalListSortingStrategy, } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Plus, ChevronDown, ChevronRight, Trash2, GripVertical, Table2, Columns3, Calendar, Clock, BarChart3, Search, Filter, X, Zap, Settings, Pin, PinOff, MessageSquare, Check, Minus, Copy, Archive, Sparkles, Link, MoreVertical, RefreshCw, Users, Type, CheckCircle2, Hash, FileText, Edit } from 'lucide-react';
import { EnhancedEditableCell } from './EnhancedEditableCell';
import { StatusColumnEditor } from './StatusColumnEditor';
import { PersonColumnEditor } from './PersonColumnEditor';
import { ColumnNameEditor } from './ColumnNameEditor';
import { ItemDetailPanel } from './ItemDetailPanel';
import { BulkEditModal } from './BulkEditModal';
import { GroupEditModal } from './GroupEditModal';
import { ConfettiAnimation } from './ConfettiAnimation';
import { ColumnSettingsModal } from './ColumnSettingsModal';
import { EnhancedKanbanView } from '../boards/views/EnhancedKanbanView';
import { CalendarView } from '../boards/views/CalendarView';
import { TimelineView } from '../boards/views/TimelineView';
import { GanttView } from '../boards/views/GanttView';
import { AppView } from '../boards/views/AppView';
import { useAppTheme } from '../../hooks/useAppTheme';
import { boardSupabaseService } from '../../services/board-supabase.service';
import { useBoardStore } from '../../store/board.store';
import { BoardHeader } from './BoardHeader';
import { BoardInfoModal } from './BoardInfoModal';
import { BoardInviteModal } from './BoardInviteModal';
import { BoardNotificationsModal } from './BoardNotificationsModal';
import { BoardImportModal } from './BoardImportModal';
import { MooPanel } from './MooPanel';
import { ActivityLogPanel } from './ActivityLogPanel';
import { supabase } from '../../lib/supabase';
// Droppable Group Header Component
const DroppableGroupHeader = ({ group, itemCount, isCollapsed, onToggle, onAddItem, onEditGroup, onDeleteGroup, isDark }) => {
    const { isOver, setNodeRef } = useDroppable({
        id: `group-${group.id}`,
    });
    return (_jsx("tr", { ref: setNodeRef, className: `group/header transition-colors ${isDark ? 'bg-[#0a1628]' : 'bg-gray-100'} ${isOver ? `ring-2 ${isDark ? 'ring-[#0ea5e9]/30 bg-[#0f1d2e]' : 'ring-[#00A5CF]/30 bg-gray-200'}` : ''}`, style: {
            position: 'sticky',
            top: 0,
            left: 0,
            zIndex: 12, // Higher than column headers
        }, children: _jsxs("td", { colSpan: 100, className: "px-4 py-3", style: {
                position: 'sticky',
                left: 0,
                top: 0,
                backgroundColor: 'transparent',
                zIndex: 12,
            }, children: [_jsxs("div", { className: "flex items-center justify-between", children: [_jsxs("button", { onClick: onToggle, className: `flex items-center space-x-2 text-sm font-medium transition-colors ${isDark ? 'text-white hover:text-gray-200' : 'text-gray-900 hover:text-gray-700'}`, children: [isCollapsed ? (_jsx(ChevronRight, { className: "w-4 h-4" })) : (_jsx(ChevronDown, { className: "w-4 h-4" })), _jsx("div", { className: "w-3 h-3 rounded-full", style: { backgroundColor: group.color } }), _jsx("span", { children: group.name }), _jsxs("span", { className: isDark ? 'text-gray-300' : 'text-gray-600', children: ["(", itemCount, ")"] })] }), _jsxs("div", { className: "flex items-center space-x-2 opacity-0 group-hover/header:opacity-100 transition-all", children: [_jsx("button", { onClick: onAddItem, className: isDark ? 'text-gray-300 hover:text-blue-400' : 'text-gray-600 hover:text-blue-600', title: "Add item to group", children: _jsx(Plus, { className: "w-4 h-4" }) }), _jsx("button", { onClick: onEditGroup, className: isDark ? 'text-gray-300 hover:text-amber-400' : 'text-gray-600 hover:text-amber-600', title: "Edit group name and color", children: _jsx(Edit, { className: "w-4 h-4" }) }), _jsx("button", { onClick: onDeleteGroup, className: isDark ? 'text-gray-300 hover:text-red-400' : 'text-gray-600 hover:text-red-600', title: "Delete group", children: _jsx(Trash2, { className: "w-4 h-4" }) })] })] }), isOver && (_jsxs("div", { className: `mt-2 text-xs font-medium ${isDark ? 'text-blue-400' : 'text-blue-600'}`, children: ["Drop item here to move to ", group.name] }))] }) }));
};
// Draggable Column Header Component
const DraggableColumnHeader = ({ column, columnIndex, isPinned, onTogglePin, onEditStatusLabels, onEditPersonColumn, onEditNumbers, onEditColumnName, pinnedOffset, isDark }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging, } = useSortable({
        id: column.id,
        disabled: isPinned // Disable dragging for pinned columns
    });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1,
        ...(isPinned && {
            position: 'sticky',
            left: `${pinnedOffset}px`,
            zIndex: 10,
            backgroundColor: 'transparent',
        }),
    };
    return (_jsx("th", { ref: setNodeRef, style: { ...style, width: column.width }, className: `group px-4 py-3 text-left text-xs font-medium uppercase tracking-wider transition-all duration-200 ${!isPinned ? 'cursor-grab active:cursor-grabbing' : ''} ${isDark ? 'text-gray-400' : 'text-gray-700'} ${isDragging
            ? `z-50 scale-105 shadow-lg ${isDark ? 'bg-blue-900/50 border-2 border-blue-400' : 'bg-blue-50 border-2 border-blue-400'}`
            : isDark ? 'hover:bg-[#0f1d2e] border-2 border-transparent' : 'hover:bg-gray-100 border-2 border-transparent'} ${isPinned ? `${isDark ? 'bg-[#0a1628] border-r border-[#0ea5e9]/20' : 'bg-gray-100 border-r border-[#00A5CF]/20'} shadow-lg` : ''}`, ...(isPinned ? {} : attributes), ...(isPinned ? {} : listeners), children: _jsxs("div", { className: "flex items-center justify-between", children: [_jsxs("div", { className: "flex items-center space-x-2", children: [_jsx("span", { className: `transition-all duration-200 ${isDragging ? 'font-semibold' : ''}`, children: column.name }), !isPinned && (_jsx(GripVertical, { className: `w-3 h-3 transition-all duration-200 ${isDragging
                                ? 'opacity-100 text-blue-600'
                                : 'opacity-0 group-hover:opacity-60 text-gray-400'}` })), isDragging && (_jsxs("div", { className: "flex items-center space-x-1 ml-2", children: [_jsx("div", { className: "w-1.5 h-1.5 bg-blue-600 rounded-full animate-pulse" }), _jsx("div", { className: "w-1.5 h-1.5 bg-blue-600 rounded-full animate-pulse", style: { animationDelay: '75ms' } }), _jsx("div", { className: "w-1.5 h-1.5 bg-blue-600 rounded-full animate-pulse", style: { animationDelay: '150ms' } })] }))] }), _jsxs("div", { className: "flex items-center gap-1", children: [column.type === 'status' && onEditStatusLabels && (_jsx("button", { onClick: () => onEditStatusLabels(column.id), className: `opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-200'} ${isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-900'}`, title: "Configure status labels and name", children: _jsx(Settings, { className: "w-3 h-3" }) })), column.type === 'person' && onEditPersonColumn && (_jsx("button", { onClick: () => onEditPersonColumn(column.id), className: `opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-200'} ${isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-900'}`, title: "Configure board members and name", children: _jsx(Users, { className: "w-3 h-3" }) })), column.type === 'numbers' && onEditNumbers && (_jsx("button", { onClick: () => onEditNumbers(column.id), className: `opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-200'} ${isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-900'}`, title: "Configure number format", children: _jsx(Settings, { className: "w-3 h-3" }) })), !['status', 'person', 'numbers'].includes(column.type) && onEditColumnName && (_jsx("button", { onClick: () => onEditColumnName?.(column.id), className: `opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-200'} ${isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-900'}`, title: "Configure column", children: _jsx(Settings, { className: "w-3 h-3" }) })), _jsx("button", { onClick: () => onTogglePin(column.id), className: `opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-200'} ${isPinned
                                ? (isDark ? 'text-blue-400' : 'text-blue-600')
                                : (isDark ? 'text-gray-400' : 'text-gray-600')}`, title: isPinned ? 'Unpin column' : 'Pin column', children: isPinned ? _jsx(Pin, { className: "w-3 h-3" }) : _jsx(PinOff, { className: "w-3 h-3" }) })] })] }) }));
};
// Draggable Item Component with EditableCell
const DraggableItem = ({ item, columns, people, updateItemData, handleDeleteItem, groupColor, pinnedColumns, getPinnedOffset, isSelected, onToggleSelection, isDark, onSelectItemDetail, onOpenDetailPanel, onCelebrate }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging, } = useSortable({ id: item.id });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };
    return (_jsxs("tr", { ref: setNodeRef, style: style, className: `group ${isDragging ? 'z-50' : ''} ${isSelected
            ? (isDark ? 'bg-cyan-900' : 'bg-blue-100')
            : (isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100')}`, children: [_jsx("td", { className: "w-12 px-4 py-3", children: _jsxs("div", { className: "flex items-center space-x-2", children: [_jsx("div", { className: "w-1 h-8 rounded-full", style: { backgroundColor: groupColor } }), _jsx("button", { onClick: (e) => onToggleSelection(item.id, e.shiftKey), className: `w-4 h-4 border rounded flex items-center justify-center transition-all ${isSelected
                                ? 'bg-blue-600 border-blue-600 text-white'
                                : (isDark ? 'border-gray-300 hover:border-blue-500' : 'border-gray-400 hover:border-blue-600')}`, children: isSelected && _jsx(Check, { className: "w-3 h-3" }) }), _jsx("button", { ...attributes, ...listeners, className: `opacity-0 group-hover:opacity-100 cursor-grab active:cursor-grabbing transition-colors ${isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`, children: _jsx(GripVertical, { className: "w-4 h-4" }) })] }) }), columns
                .sort((a, b) => a.position - b.position)
                .map((column, columnIndex) => {
                const isPinned = pinnedColumns.includes(column.id);
                const pinnedOffset = isPinned ? getPinnedOffset(columnIndex, columns) : 0;
                return (_jsx("td", { className: `px-4 py-1 ${isPinned ? (isDark ? 'bg-gray-800 border-r border-white/20' : 'bg-gray-100 border-r border-gray-300') : ''}`, style: {
                        width: column.width,
                        ...(isPinned && {
                            position: 'sticky',
                            left: `${pinnedOffset}px`,
                            zIndex: 8,
                            backgroundColor: 'transparent',
                        }),
                    }, children: column.id === 'col-1' ? (
                    // Special Monday.com-style item name cell with update button
                    _jsxs("div", { className: "flex items-center space-x-2 min-h-8", children: [_jsx("div", { className: "flex-1", children: _jsx(EnhancedEditableCell, { column: column, value: item.data[column.id], onSave: (value) => updateItemData(item.id, column.id, value), people: people, onCelebrate: onCelebrate }) }), _jsx("button", { onClick: () => {
                                    onSelectItemDetail(item.id);
                                    onOpenDetailPanel();
                                }, className: `opacity-0 group-hover:opacity-100 p-1 rounded transition-all ${isDark
                                    ? 'hover:bg-blue-500/20 text-gray-300 hover:text-blue-400'
                                    : 'hover:bg-blue-500/10 text-gray-600 hover:text-blue-600'}`, title: "View updates and details", children: _jsx(MessageSquare, { className: "w-4 h-4" }) })] })) : (_jsx(EnhancedEditableCell, { column: column, value: item.data[column.id], onSave: (value) => updateItemData(item.id, column.id, value), people: people, onCelebrate: onCelebrate })) }, column.id));
            }), _jsx("td", { className: "w-12 px-4 py-3", children: _jsx("button", { onClick: () => handleDeleteItem(item.id), className: `opacity-0 group-hover:opacity-100 transition-all ${isDark ? 'text-gray-400 hover:text-red-400' : 'text-gray-500 hover:text-red-600'}`, children: _jsx(Trash2, { className: "w-4 h-4" }) }) })] }));
};
export const Board = ({ boardId, boardData, onUpdate, title = 'Insert Board Name', description = 'Write description here...' }) => {
    const { isDark } = useAppTheme();
    const { updateBoard } = useBoardStore();
    const [currentView, setCurrentView] = useState('table');
    const [searchQuery, setSearchQuery] = useState('');
    const [activeFilters, setActiveFilters] = useState({});
    const [showFilters, setShowFilters] = useState(false);
    const [showAutomation, setShowAutomation] = useState(false);
    // Default columns for board
    const [columns, setColumns] = useState([
        { id: 'col-1', name: 'Item', type: 'text', width: '250px', position: 0, isPinned: true },
        { id: 'col-2', name: 'Status', type: 'status', width: '150px', position: 1,
            options: [
                { id: '1', label: 'Not Started', color: '#c4c4c4' },
                { id: '2', label: 'In Progress', color: '#fdab3d' },
                { id: '3', label: 'Done', color: '#00c875' },
            ]
        },
        { id: 'col-3', name: 'Person', type: 'person', width: '150px', position: 2 },
        { id: 'col-4', name: 'Due Date', type: 'date', width: '150px', position: 3 },
    ]);
    const [groups, setGroups] = useState([]);
    const [items, setItems] = useState([]);
    const [collapsedGroups, setCollapsedGroups] = useState([]);
    const [activeId, setActiveId] = useState(null);
    const [pinnedColumns, setPinnedColumns] = useState(['col-1']);
    const [selectedItems, setSelectedItems] = useState([]);
    const [showBulkActions, setShowBulkActions] = useState(false);
    const [lastClickedIndex, setLastClickedIndex] = useState(null);
    const [groupByColumn, setGroupByColumn] = useState(null);
    const [groupBySorting, setGroupBySorting] = useState('asc');
    const [showGroupByDropdown, setShowGroupByDropdown] = useState(false);
    const [groupByDropdownPosition, setGroupByDropdownPosition] = useState({ top: 0, left: 0 });
    const groupByTriggerRef = useRef(null);
    const groupByDropdownRef = useRef(null);
    const [savedViews, setSavedViews] = useState([
        { id: 'table', name: 'Table', isDefault: true, config: {} }
    ]);
    const [activeView, setActiveView] = useState('table');
    const [showSaveViewModal, setShowSaveViewModal] = useState(false);
    const [showViewsDropdown, setShowViewsDropdown] = useState(false);
    const [showAddColumnModal, setShowAddColumnModal] = useState(false);
    const [showStatusEditor, setShowStatusEditor] = useState(false);
    const [editingStatusColumnId, setEditingStatusColumnId] = useState(null);
    const [showPersonEditor, setShowPersonEditor] = useState(false);
    const [editingPersonColumnId, setEditingPersonColumnId] = useState(null);
    const [showColumnNameEditor, setShowColumnNameEditor] = useState(false);
    const [editingColumnNameId, setEditingColumnNameId] = useState(null);
    const [showDetailPanel, setShowDetailPanel] = useState(false);
    const [selectedItemId, setSelectedItemId] = useState(null);
    const [showBulkEditModal, setShowBulkEditModal] = useState(false);
    const [showGroupEditModal, setShowGroupEditModal] = useState(false);
    const [editingGroup, setEditingGroup] = useState(null);
    const [triggerConfetti, setTriggerConfetti] = useState(false);
    const [showColumnSettingsModal, setShowColumnSettingsModal] = useState(false);
    const [editingColumnForSettings, setEditingColumnForSettings] = useState(null);
    // Board data from Supabase
    const [boardInfo, setBoardInfo] = useState(null);
    const [loadingBoard, setLoadingBoard] = useState(false);
    // Editable title and description state
    const [isEditingTitle, setIsEditingTitle] = useState(false);
    const [isEditingDescription, setIsEditingDescription] = useState(false);
    const [editTitle, setEditTitle] = useState(title);
    const [editDescription, setEditDescription] = useState(description);
    const titleInputRef = useRef(null);
    const descriptionInputRef = useRef(null);
    // Board management state
    const [showBoardInfo, setShowBoardInfo] = useState(false);
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [showNotificationsModal, setShowNotificationsModal] = useState(false);
    const [boardMembers, setBoardMembers] = useState([]);
    const [boardType, setBoardType] = useState('main');
    const [currentUser, setCurrentUser] = useState(null);
    const [loadingMembers, setLoadingMembers] = useState(false);
    // Moo AI Assistant state
    const [showMooPanel, setShowMooPanel] = useState(false);
    // Top header bar states
    const [showSidekickModal, setShowSidekickModal] = useState(false);
    const [showIntegrateModal, setShowIntegrateModal] = useState(false);
    const [showMenuDropdown, setShowMenuDropdown] = useState(false);
    const [showActivityLog, setShowActivityLog] = useState(false);
    const [showMoreActionsSubmenu, setShowMoreActionsSubmenu] = useState(false);
    const [showImportModal, setShowImportModal] = useState(false);
    // Convert board members to people array for assignee picker
    const people = useMemo(() => {
        return boardMembers.map(member => ({
            id: member.user_id,
            name: member.user_name,
            color: member.user_avatar ? '#0073ea' : '#00c875', // Use avatar presence to vary colors
            avatar: member.user_avatar || undefined,
        }));
    }, [boardMembers]);
    // Column pinning utility functions
    const toggleColumnPin = (columnId) => {
        setPinnedColumns(prev => prev.includes(columnId)
            ? prev.filter(id => id !== columnId)
            : [...prev, columnId]);
        setColumns(prev => prev.map(col => col.id === columnId
            ? { ...col, isPinned: !col.isPinned }
            : col));
    };
    const getPinnedOffset = (columnIndex, columns) => {
        let offset = 0;
        const sortedColumns = columns.sort((a, b) => a.position - b.position);
        for (let i = 0; i < columnIndex; i++) {
            if (sortedColumns[i]?.isPinned) {
                offset += parseInt(sortedColumns[i].width.replace('px', '')) || 150;
            }
        }
        return offset;
    };
    // Handle status label changes
    const handleStatusLabelsChange = async (newLabels) => {
        if (!editingStatusColumnId)
            return;
        setColumns(prev => prev.map(col => col.id === editingStatusColumnId
            ? { ...col, options: newLabels }
            : col));
        // Save to Supabase (but don't close the modal - let user click Done)
        try {
            if (boardInfo) {
                const updatedColumns = columns.map(col => col.id === editingStatusColumnId
                    ? { ...col, options: newLabels }
                    : col);
                // Build columns_config as JSONB with full column objects
                const columnsConfig = updatedColumns.reduce((acc, col) => {
                    acc[col.id] = col;
                    return acc;
                }, {});
                // Build column_order array
                const columnOrder = updatedColumns.map(col => col.id);
                const { error } = await supabase
                    .from('boards')
                    .update({ columns_config: columnsConfig, column_order: columnOrder })
                    .eq('id', boardInfo.id);
                if (error) {
                    console.error('Error saving column configuration:', error);
                }
                else {
                    console.log('Status labels updated successfully');
                }
            }
        }
        catch (error) {
            console.error('Error updating status labels:', error);
        }
    };
    // Helper function to convert status column value to status string
    const getStatusFromValue = (value) => {
        const statusColumn = columns.find(col => col.id === 'col-2');
        if (!value || !statusColumn?.options)
            return 'todo';
        const option = statusColumn.options.find(opt => opt.id === value);
        if (!option)
            return 'todo';
        const label = option.label.toLowerCase();
        if (label.includes('done') || label.includes('completed'))
            return 'completed';
        if (label.includes('progress') || label.includes('working'))
            return 'in_progress';
        if (label.includes('review'))
            return 'review';
        if (label.includes('blocked'))
            return 'blocked';
        return 'todo';
    };
    const pinnedColumnsWidth = columns
        .filter(col => col.isPinned)
        .reduce((total, col) => total + (parseInt(col.width.replace('px', '')) || 150), 0);
    // Filter and search functionality
    const filteredItems = items.filter(item => {
        // Search filter
        if (searchQuery) {
            const searchLower = searchQuery.toLowerCase();
            const matchesSearch = Object.values(item.data).some(value => String(value).toLowerCase().includes(searchLower));
            if (!matchesSearch)
                return false;
        }
        // Status filter
        if (activeFilters.status && item.data['col-2'] !== activeFilters.status) {
            return false;
        }
        // Person filter
        if (activeFilters.person && item.data['col-4'] !== activeFilters.person) {
            return false;
        }
        return true;
    });
    // Group By functionality - dynamically create groups based on selected column
    const dynamicGroups = useMemo(() => {
        if (!groupByColumn) {
            return groups; // Use original groups if no grouping is applied
        }
        // Create groups based on column values
        const groupMap = new Map();
        filteredItems.forEach(item => {
            const value = item.data[groupByColumn];
            const groupKey = value || 'Empty'; // Handle empty values
            if (!groupMap.has(groupKey)) {
                groupMap.set(groupKey, []);
            }
            groupMap.get(groupKey).push(item);
        });
        // Convert to group structure
        const newGroups = [];
        const sortedGroupKeys = Array.from(groupMap.keys()).sort((a, b) => {
            if (groupBySorting === 'desc') {
                return b.localeCompare(a);
            }
            return a.localeCompare(b);
        });
        sortedGroupKeys.forEach((groupKey, index) => {
            const groupItems = groupMap.get(groupKey);
            const selectedColumn = columns.find(col => col.id === groupByColumn);
            // Generate color based on group key
            const colors = ['#ff6b6b', '#4ecdc4', '#45b7d1', '#96ceb4', '#ffeaa7', '#dda0dd', '#98d8c8'];
            const color = colors[index % colors.length];
            newGroups.push({
                id: `dynamic-group-${groupKey}`,
                name: selectedColumn?.type === 'status'
                    ? (selectedColumn.options?.find(opt => opt.id === groupKey)?.label || groupKey)
                    : selectedColumn?.type === 'person'
                        ? (people.find(p => p.id === groupKey)?.name || 'Unassigned')
                        : String(groupKey),
                color,
                position: index,
                collapsed: false
            });
        });
        return newGroups;
    }, [groupByColumn, filteredItems, groups, columns, people, groupBySorting]);
    // Update items to use dynamic groups
    const processedItems = useMemo(() => {
        if (!groupByColumn) {
            return filteredItems; // Use original items if no grouping
        }
        return filteredItems.map(item => {
            const value = item.data[groupByColumn];
            const groupKey = value || 'Empty';
            return {
                ...item,
                groupId: `dynamic-group-${groupKey}`
            };
        });
    }, [groupByColumn, filteredItems]);
    // View management functions
    const getCurrentViewConfig = () => ({
        groupByColumn,
        groupBySorting,
        activeFilters,
        searchQuery,
        viewType: currentView,
        hiddenColumns: [] // We can add hidden columns functionality later
    });
    const applyViewConfig = (config) => {
        if (config.groupByColumn !== undefined)
            setGroupByColumn(config.groupByColumn);
        if (config.groupBySorting)
            setGroupBySorting(config.groupBySorting);
        if (config.activeFilters)
            setActiveFilters(config.activeFilters);
        if (config.searchQuery !== undefined)
            setSearchQuery(config.searchQuery);
        if (config.viewType)
            setCurrentView(config.viewType);
    };
    const saveCurrentView = (name) => {
        const viewConfig = getCurrentViewConfig();
        const newView = {
            id: `view-${Date.now()}`,
            name,
            config: viewConfig
        };
        setSavedViews(prev => [...prev, newView]);
        setActiveView(newView.id);
        setShowSaveViewModal(false);
    };
    const loadView = (viewId) => {
        const view = savedViews.find(v => v.id === viewId);
        if (view) {
            applyViewConfig(view.config);
            setActiveView(viewId);
        }
    };
    const deleteView = (viewId) => {
        if (viewId === 'default')
            return; // Can't delete default view
        setSavedViews(prev => prev.filter(v => v.id !== viewId));
        if (activeView === viewId) {
            setActiveView('default');
            loadView('default');
        }
    };
    // Group summary calculations
    const calculateGroupSummaries = (groupItems, columns) => {
        const summaries = {};
        columns.forEach(column => {
            if (column.type === 'number') {
                const numericValues = groupItems
                    .map(item => parseFloat(item.data[column.id]))
                    .filter(val => !isNaN(val));
                if (numericValues.length > 0) {
                    summaries[column.id] = {
                        sum: numericValues.reduce((acc, val) => acc + val, 0),
                        average: numericValues.reduce((acc, val) => acc + val, 0) / numericValues.length,
                        min: Math.min(...numericValues),
                        max: Math.max(...numericValues),
                        count: numericValues.length
                    };
                }
            }
            else if (column.type === 'text' || column.type === 'status' || column.type === 'person') {
                // For non-numeric columns, just show count
                summaries[column.id] = {
                    count: groupItems.filter(item => item.data[column.id] && item.data[column.id] !== '').length
                };
            }
        });
        return summaries;
    };
    // Selection utility functions
    const toggleItemSelection = (itemId, shiftKey = false) => {
        const clickedIndex = processedItems.findIndex(item => item.id === itemId);
        if (shiftKey && lastClickedIndex !== null && clickedIndex !== -1) {
            // Range selection: select all items between last clicked and current
            const start = Math.min(lastClickedIndex, clickedIndex);
            const end = Math.max(lastClickedIndex, clickedIndex);
            const rangeIds = processedItems.slice(start, end + 1).map(item => item.id);
            setSelectedItems(prev => {
                // Combine existing selections with range
                const newSet = new Set([...prev, ...rangeIds]);
                return Array.from(newSet);
            });
        }
        else {
            // Single toggle
            setSelectedItems(prev => prev.includes(itemId)
                ? prev.filter(id => id !== itemId)
                : [...prev, itemId]);
        }
        // Update last clicked index for future range selections
        if (clickedIndex !== -1) {
            setLastClickedIndex(clickedIndex);
        }
    };
    const selectAllItems = () => {
        setSelectedItems(processedItems.map(item => item.id));
    };
    const deselectAllItems = () => {
        setSelectedItems([]);
    };
    const isAllSelected = selectedItems.length === processedItems.length && processedItems.length > 0;
    const isPartiallySelected = selectedItems.length > 0 && selectedItems.length < processedItems.length;
    // Bulk action functions
    const handleBulkDelete = () => {
        if (window.confirm(`Delete ${selectedItems.length} selected items?`)) {
            setItems(prev => prev.filter(item => !selectedItems.includes(item.id)));
            setSelectedItems([]);
        }
    };
    const handleBulkDuplicate = () => {
        const itemsToDuplicate = items.filter(item => selectedItems.includes(item.id));
        const newItems = itemsToDuplicate.map(item => ({
            ...item,
            id: uuid(),
            data: { ...item.data, [columns[0]?.id]: `${item.data[columns[0]?.id]} (Copy)` },
        }));
        setItems(prev => [...prev, ...newItems]);
        setSelectedItems([]);
    };
    const handleBulkArchive = () => {
        console.log('Archive items:', selectedItems);
        // Implement archive functionality
        setSelectedItems([]);
    };
    // Calculate Group By dropdown position
    const calculateGroupByDropdownPosition = (triggerElement) => {
        const rect = triggerElement.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const viewportWidth = window.innerWidth;
        const dropdownWidth = 256; // w-64 = 16rem = 256px
        const dropdownMaxHeight = 400; // Approximate max height
        let top = rect.bottom + 4; // Default: below trigger
        let left = rect.left; // Default: align left edge
        // Adjust if dropdown would go off bottom of screen
        if (top + dropdownMaxHeight > viewportHeight) {
            top = Math.max(8, viewportHeight - dropdownMaxHeight - 8);
        }
        // Adjust if dropdown would go off right of screen
        if (left + dropdownWidth > viewportWidth - 8) {
            left = viewportWidth - dropdownWidth - 8;
        }
        // Adjust if dropdown would go off left of screen
        if (left < 8) {
            left = 8;
        }
        return { top, left };
    };
    // Show bulk actions when items are selected
    React.useEffect(() => {
        setShowBulkActions(selectedItems.length > 0);
    }, [selectedItems]);
    // Keyboard shortcuts for selection
    React.useEffect(() => {
        const handleKeyDown = (event) => {
            // Ctrl+A / Cmd+A - Select all
            if ((event.ctrlKey || event.metaKey) && event.key === 'a') {
                event.preventDefault();
                selectAllItems();
            }
            // Delete - Delete selected items
            if (event.key === 'Delete' && selectedItems.length > 0) {
                event.preventDefault();
                handleBulkDelete();
            }
            // Escape - Clear selection
            if (event.key === 'Escape' && selectedItems.length > 0) {
                event.preventDefault();
                deselectAllItems();
            }
            // Ctrl+D / Cmd+D - Duplicate selected items
            if ((event.ctrlKey || event.metaKey) && event.key === 'd' && selectedItems.length > 0) {
                event.preventDefault();
                handleBulkDuplicate();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [selectedItems, handleBulkDelete, handleBulkDuplicate, selectAllItems, deselectAllItems]);
    // Close Group By dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            const target = event.target;
            const clickedOutside = groupByDropdownRef.current && !groupByDropdownRef.current.contains(target) &&
                groupByTriggerRef.current && !groupByTriggerRef.current.contains(target);
            if (clickedOutside) {
                setShowGroupByDropdown(false);
            }
        };
        if (showGroupByDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [showGroupByDropdown]);
    // Define fetchBoardData as useCallback so it can be called from multiple places
    const fetchBoardData = useCallback(async () => {
        // Use boardId prop directly instead of boardData?.boardId
        const actualBoardId = boardId || boardData?.boardId;
        if (!actualBoardId)
            return;
        setLoadingBoard(true);
        try {
            const board = await boardSupabaseService.getBoardById(actualBoardId);
            console.log('[Board] Fetched board:', board);
            if (board) {
                setBoardInfo(board);
                // Use fetched title if available, otherwise use the default from props
                const boardTitle = board.title && board.title.trim() ? board.title : title;
                setEditTitle(boardTitle);
                setEditDescription(board.description || '');
                console.log('[Board] Set title to:', boardTitle);
                // Load columns from columns_config if available
                // First, get the raw board data with columns_config
                const { data: rawBoardData } = await supabase
                    .from('boards')
                    .select('columns_config, column_order, available_columns')
                    .eq('id', actualBoardId)
                    .single();
                console.log('[Board] Raw data from Supabase:', {
                    hasColumnsConfig: !!rawBoardData?.columns_config,
                    columnsConfigType: typeof rawBoardData?.columns_config,
                    columnsConfigValue: rawBoardData?.columns_config,
                    columnOrderType: typeof rawBoardData?.column_order,
                    columnOrderValue: rawBoardData?.column_order,
                });
                if (rawBoardData?.columns_config) {
                    // Handle all cases: string, array, or object - must be object!
                    let columnsConfig;
                    if (typeof rawBoardData.columns_config === 'string') {
                        console.log('[Board] ⚠️ columns_config is a string, parsing JSON...');
                        try {
                            columnsConfig = JSON.parse(rawBoardData.columns_config);
                            // Verify parsed result is object, not array
                            if (Array.isArray(columnsConfig)) {
                                console.error('[Board] ❌ Parsed JSON is array instead of object, using empty object');
                                columnsConfig = {};
                            }
                        }
                        catch (e) {
                            console.error('[Board] Failed to parse columns_config JSON:', e);
                            columnsConfig = {};
                        }
                    }
                    else if (Array.isArray(rawBoardData.columns_config)) {
                        console.error('[Board] ❌ columns_config is an array (should be object), starting fresh');
                        columnsConfig = {};
                    }
                    else {
                        columnsConfig = rawBoardData.columns_config;
                    }
                    const columnOrder = (rawBoardData.column_order && Array.isArray(rawBoardData.column_order))
                        ? rawBoardData.column_order
                        : (columnsConfig ? Object.keys(columnsConfig) : []);
                    console.log('[Board] Processing columns:', {
                        columnsConfigKeys: Object.keys(columnsConfig),
                        columnOrderArray: columnOrder,
                        columnsConfigKeysCount: Object.keys(columnsConfig).length,
                        columnOrderCount: columnOrder?.length || 0,
                    });
                    // Reconstruct columns from config
                    const loadedColumns = columnOrder
                        .map((colId) => columnsConfig[colId])
                        .filter((col) => col !== undefined && col !== null);
                    console.log('[Board] Loaded columns from config:', {
                        loadedColumnsCount: loadedColumns.length,
                        loadedColumns: loadedColumns,
                    });
                    if (loadedColumns.length > 0) {
                        // Get existing default columns
                        const defaultCols = columns;
                        // Identify which loaded columns are custom (not in default set)
                        const customColumns = loadedColumns.filter(col => !['col-1', 'col-2', 'col-3', 'col-4'].includes(col.id));
                        console.log('[Board] Custom columns to add:', {
                            customColumnsCount: customColumns.length,
                            customColumns: customColumns.map(c => ({ id: c.id, name: c.name })),
                        });
                        if (customColumns.length > 0) {
                            // Add custom columns to the end of default columns
                            const mergedColumns = [...defaultCols, ...customColumns];
                            setColumns(mergedColumns);
                            console.log('[Board] Merged columns (defaults + custom):', {
                                totalColumns: mergedColumns.length,
                                columns: mergedColumns.map(c => ({ id: c.id, name: c.name })),
                            });
                        }
                    }
                }
                // Populate groups from Supabase
                const boardGroups = board.groups && board.groups.length > 0 ? board.groups : [];
                if (boardGroups.length > 0) {
                    setGroups(boardGroups.map(group => ({
                        id: group.id,
                        name: group.title,
                        color: group.color,
                        position: group.position,
                        collapsed: group.isCollapsed || false
                    })));
                    // Populate items from board tasks
                    const items = [];
                    boardGroups.forEach(group => {
                        (group.tasks || []).forEach((task, index) => {
                            items.push({
                                id: task.id,
                                groupId: group.id,
                                position: index,
                                data: {
                                    'col-1': task.title,
                                    'col-2': task.status ? ['1', '2', '3'].find(id => (id === '1' && task.status === 'Not Started') ||
                                        (id === '2' && task.status === 'In Progress') ||
                                        (id === '3' && task.status === 'Done')) || task.status : '',
                                    'col-3': task.assigneeIds?.[0] || '',
                                    'col-4': task.dueDate ? new Date(task.dueDate).toISOString().split('T')[0] : ''
                                }
                            });
                        });
                    });
                    setItems(items);
                }
                else {
                    // Create default groups in Supabase if none exist
                    const defaultGroupNames = ['Todo', 'In Progress', 'Done'];
                    const defaultColors = ['#579bfc', '#ff9800', '#00c875'];
                    const createdGroups = [];
                    for (let i = 0; i < defaultGroupNames.length; i++) {
                        const createdGroup = await boardSupabaseService.createGroup(actualBoardId, {
                            title: defaultGroupNames[i],
                            color: defaultColors[i],
                            position: i,
                            isCollapsed: false
                        });
                        if (createdGroup) {
                            createdGroups.push({
                                id: createdGroup.id,
                                name: createdGroup.title,
                                color: createdGroup.color,
                                position: createdGroup.position,
                                collapsed: createdGroup.isCollapsed || false
                            });
                        }
                    }
                    setGroups(createdGroups);
                }
            }
        }
        catch (error) {
            console.error('Failed to fetch board:', error);
        }
        finally {
            setLoadingBoard(false);
        }
    }, [boardId, boardData?.boardId, title]);
    // Fetch board data from Supabase when boardId is provided
    useEffect(() => {
        fetchBoardData();
        // Get current user and load members
        supabase.auth.getUser().then(({ data: { user } }) => {
            if (user) {
                const currentUserData = {
                    id: user.id,
                    email: user.email,
                    name: user.user_metadata?.full_name || 'User',
                    avatar: user.user_metadata?.avatar_url
                };
                setCurrentUser(currentUserData);
                // Load members with current user for fallback logic
                loadBoardMembers(currentUserData);
            }
            else {
                // Load members even if no user
                loadBoardMembers();
            }
        });
    }, [fetchBoardData]);
    // Reset confetti trigger after animation completes
    useEffect(() => {
        if (triggerConfetti) {
            const timer = setTimeout(() => {
                setTriggerConfetti(false);
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [triggerConfetti]);
    const sensors = useSensors(useSensor(PointerSensor, {
        activationConstraint: {
            distance: 8,
        },
    }));
    const updateItemData = useCallback((itemId, columnId, value) => {
        // Update local state first for immediate UI response
        setItems(prevItems => prevItems.map(item => item.id === itemId
            ? { ...item, data: { ...item.data, [columnId]: value } }
            : item));
        // Save to Supabase asynchronously
        if (boardId && boardInfo) {
            const item = items.find(i => i.id === itemId);
            if (item) {
                const updatedData = { ...item.data, [columnId]: value };
                // Map column data to task fields
                const taskUpdates = {
                    title: updatedData['col-1'] || 'Untitled',
                    status: updatedData['col-2'] ?
                        (['1', '2', '3'].includes(updatedData['col-2'])
                            ? { '1': 'Not Started', '2': 'In Progress', '3': 'Done' }[updatedData['col-2']]
                            : updatedData['col-2'])
                        : 'Not Started',
                };
                if (updatedData['col-3']) {
                    taskUpdates.assigneeIds = [updatedData['col-3']];
                }
                if (updatedData['col-4']) {
                    // Handle date as ISO string
                    taskUpdates.dueDate = updatedData['col-4'];
                }
                // Save to Supabase without blocking UI
                boardSupabaseService.updateTask(boardId, item.groupId, itemId, taskUpdates)
                    .catch(error => {
                    console.error('Failed to update item in Supabase:', error);
                });
            }
        }
        // Notify parent component of updates
        if (onUpdate) {
            onUpdate({ items, columns, groups });
        }
    }, [items, columns, groups, onUpdate, boardId, boardInfo]);
    // Editable title and description handlers
    const handleTitleEdit = () => {
        setIsEditingTitle(true);
        setTimeout(() => titleInputRef.current?.focus(), 0);
    };
    const handleDescriptionEdit = () => {
        setIsEditingDescription(true);
        setTimeout(() => descriptionInputRef.current?.focus(), 0);
    };
    const handleTitleSave = async () => {
        if (editTitle.trim() && boardInfo) {
            setIsEditingTitle(false);
            // Update in Supabase
            try {
                const updated = await boardSupabaseService.updateBoard(boardInfo.id, {
                    title: editTitle,
                });
                if (updated) {
                    setBoardInfo(updated);
                }
            }
            catch (error) {
                console.error('Failed to update board title:', error);
                // Revert on error
                setEditTitle(boardInfo.title);
            }
            // Update store to sync with sidebar and floating nav
            await updateBoard(boardInfo.id, { title: editTitle });
            // Also notify parent if onUpdate is provided
            if (onUpdate) {
                onUpdate({ title: editTitle });
            }
        }
    };
    const handleDescriptionSave = async () => {
        if (editDescription.trim() && boardInfo) {
            setIsEditingDescription(false);
            // Update in Supabase
            try {
                const updated = await boardSupabaseService.updateBoard(boardInfo.id, {
                    description: editDescription,
                });
                if (updated) {
                    setBoardInfo(updated);
                }
            }
            catch (error) {
                console.error('Failed to update board description:', error);
                // Revert on error
                setEditDescription(boardInfo.description || '');
            }
            // Also notify parent if onUpdate is provided
            if (onUpdate) {
                onUpdate({ description: editDescription });
            }
        }
    };
    const handleTitleKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleTitleSave();
        }
        else if (e.key === 'Escape') {
            setEditTitle(boardInfo?.title || title);
            setIsEditingTitle(false);
        }
    };
    const handleDescriptionKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleDescriptionSave();
        }
        else if (e.key === 'Escape') {
            setEditDescription(boardInfo?.description || description);
            setIsEditingDescription(false);
        }
    };
    // BoardHeader handlers
    const handleTitleChange = (newTitle) => {
        setEditTitle(newTitle);
        if (boardInfo) {
            // Update in Supabase with better error handling
            boardSupabaseService.updateBoard(boardInfo.id, { title: newTitle })
                .then(updated => {
                if (updated) {
                    setBoardInfo(updated);
                    console.log('[Board] Title updated successfully:', newTitle);
                }
                else {
                    console.error('[Board] Failed to update board title - no response');
                    setEditTitle(boardInfo.title);
                }
            })
                .catch(err => {
                console.error('[Board] Failed to update board title:', err);
                // Revert the title on error
                setEditTitle(boardInfo.title);
            });
            if (onUpdate) {
                onUpdate({ title: newTitle });
            }
        }
        else {
            console.warn('[Board] No board info loaded - cannot save title');
        }
    };
    const handleDescriptionChange = (newDescription) => {
        setEditDescription(newDescription);
        if (boardInfo) {
            // Update in Supabase
            boardSupabaseService.updateBoard(boardInfo.id, { description: newDescription }).then(updated => {
                if (updated) {
                    setBoardInfo(updated);
                }
            }).catch(err => {
                console.error('Failed to update board description:', err);
                setEditDescription(boardInfo.description || '');
            });
            if (onUpdate) {
                onUpdate({ description: newDescription });
            }
        }
    };
    // Board member management handlers
    const loadBoardMembers = async (currentUserForFallback) => {
        if (!boardId) {
            console.warn('[Board] Cannot load members - no boardId provided');
            return;
        }
        setLoadingMembers(true);
        try {
            const members = await boardSupabaseService.getBoardMembers(boardId);
            console.log('[Board] Loaded members:', members);
            setBoardMembers(members || []);
            // If no members exist, add current user as owner (fallback for boards created before member auto-add)
            if ((!members || members.length === 0) && currentUserForFallback) {
                console.log('[Board] No members found, adding current user as owner');
                try {
                    await boardSupabaseService.addBoardMember(boardId, currentUserForFallback.id, currentUserForFallback.name, currentUserForFallback.avatar || null, 'owner');
                    // Reload members after adding
                    const updatedMembers = await boardSupabaseService.getBoardMembers(boardId);
                    setBoardMembers(updatedMembers || []);
                }
                catch (addError) {
                    console.error('[Board] Failed to add current user as board member:', addError);
                }
            }
        }
        catch (error) {
            console.error('Failed to load board members:', error);
        }
        finally {
            setLoadingMembers(false);
        }
    };
    const handleInviteMember = async (userId, userName, userAvatar, role) => {
        if (!boardId || !currentUser)
            return;
        try {
            // Use the new invitation flow instead of directly adding members
            await boardSupabaseService.inviteBoardMember(boardId, userId, currentUser.id, boardInfo?.title || 'Board', role);
            await loadBoardMembers();
        }
        catch (error) {
            console.error('Failed to invite member:', error);
            throw error;
        }
    };
    const handleRemoveMember = async (userId) => {
        if (!boardId)
            return;
        try {
            await boardSupabaseService.removeBoardMember(boardId, userId);
            await loadBoardMembers();
        }
        catch (error) {
            console.error('Failed to remove member:', error);
            throw error;
        }
    };
    const handleUpdateMemberRole = async (userId, role) => {
        if (!boardId)
            return;
        try {
            await boardSupabaseService.updateBoardMemberRole(boardId, userId, role);
            await loadBoardMembers();
        }
        catch (error) {
            console.error('Failed to update member role:', error);
            throw error;
        }
    };
    const handleBoardTypeChange = (newType) => {
        if (!boardInfo)
            return;
        setBoardType(newType);
        // Note: COWBoard doesn't have a 'type' property yet, this can be added later
        // boardSupabaseService.updateBoard(boardInfo.id, { type: newType }).then(updated => {
        //   if (updated) {
        //     setBoardInfo(updated);
        //   }
        // }).catch(err => {
        //   console.error('Failed to update board type:', err);
        //   setBoardType(boardType);
        // });
    };
    // Convert board items to tasks for Kanban view
    const convertItemsToTasks = () => {
        return items.map(item => ({
            id: item.id,
            title: item.data['col-1'] || 'Untitled',
            description: item.data['col-3'] || '',
            status: item.data['col-2'] || 'todo',
            position: item.position,
            assignedTo: item.data['col-4'] || null,
            projectId: 'board-project',
            createdAt: new Date(),
            updatedAt: new Date(),
            dueDate: item.data['col-5'] ? new Date(item.data['col-5']) : null,
        }));
    };
    const handleTaskUpdate = (taskId, updates) => {
        const columnMappings = {
            status: 'col-2',
            title: 'col-1',
            description: 'col-3',
            assignedTo: 'col-4',
            dueDate: 'col-5',
        };
        setItems(prevItems => prevItems.map(item => {
            if (item.id === taskId) {
                const newData = { ...item.data };
                Object.entries(updates).forEach(([key, value]) => {
                    const columnId = columnMappings[key];
                    if (columnId) {
                        newData[columnId] = value;
                    }
                });
                return { ...item, data: newData, position: updates.position ?? item.position };
            }
            return item;
        }));
    };
    const handleTaskCreate = (task) => {
        const newItem = {
            id: uuid(),
            groupId: groups[0]?.id || 'group-1',
            position: items.filter(item => item.groupId === groups[0]?.id).length,
            data: {
                'col-1': task.title || 'New Item',
                'col-2': task.status || 'todo',
                'col-3': task.description || '',
                'col-4': task.assignedTo || '',
                'col-5': task.dueDate || '',
            },
        };
        setItems(prev => [...prev, newItem]);
    };
    const handleTaskDelete = (taskId) => {
        setItems(prev => prev.filter(item => item.id !== taskId));
    };
    const clearFilters = () => {
        setActiveFilters({});
        setSearchQuery('');
    };
    const hasActiveFilters = searchQuery || Object.keys(activeFilters).length > 0;
    const toggleGroup = (groupId) => {
        setCollapsedGroups(prev => prev.includes(groupId)
            ? prev.filter(id => id !== groupId)
            : [...prev, groupId]);
    };
    const handleAddItem = async (groupId) => {
        // Save to Supabase first to get the real ID
        if (boardId && boardInfo) {
            try {
                const createdTask = await boardSupabaseService.createTask(boardId, groupId, {
                    title: 'New Item',
                    status: 'Not Started',
                    position: items.filter(item => item.groupId === groupId).length,
                    updatedByUserId: currentUser?.id
                });
                if (createdTask) {
                    // Use the ID from the database
                    const newItem = {
                        id: createdTask.id,
                        groupId,
                        position: items.filter(item => item.groupId === groupId).length,
                        data: { [columns[0]?.id]: 'New Item' },
                    };
                    setItems(prev => [...prev, newItem]);
                    console.log('Item created successfully in Supabase');
                }
            }
            catch (error) {
                console.error('Failed to create item in Supabase:', error);
            }
        }
        else {
            // Fallback for local-only mode
            const newItem = {
                id: uuid(),
                groupId,
                position: items.filter(item => item.groupId === groupId).length,
                data: { [columns[0]?.id]: 'New Item' },
            };
            setItems(prev => [...prev, newItem]);
        }
    };
    const handleAddColumn = async (columnData) => {
        const newColumn = {
            id: `col-${Date.now()}`,
            name: columnData.name,
            type: columnData.type,
            width: '180px',
            position: columns.length,
            ...(columnData.type === 'status' && {
                options: [
                    { id: 'option-1', label: 'Not Started', color: '#c4c4c4' },
                    { id: 'option-2', label: 'Working on it', color: '#fdab3d' },
                    { id: 'option-3', label: 'Done', color: '#00c875' }
                ]
            }),
            ...(columnData.type === 'files' && {
                defaultValue: { files: [] }
            }),
            ...(columnData.type === 'timeline' && {
                defaultValue: { startDate: new Date().toISOString(), endDate: new Date().toISOString(), progress: 0, status: 'not-started' }
            }),
            ...(columnData.type === 'numbers' && {
                numberFormat: { format: 'integer' }
            }),
            ...(columnData.type === 'time-tracker' && {
                defaultValue: { totalSeconds: 0, isRunning: false, sessions: [] }
            })
        };
        // Update local state first
        setColumns(prev => [...prev, newColumn]);
        setShowAddColumnModal(false);
        // Open settings modal for newly created column
        setEditingColumnForSettings(newColumn);
        setShowColumnSettingsModal(true);
        console.log('[Column Created]', { columnId: newColumn.id, name: newColumn.name, type: newColumn.type });
        // Save to Supabase
        if (boardId && boardInfo) {
            try {
                const updatedColumns = [...columns, newColumn];
                // Build columns_config as JSONB with full column objects
                const columnsConfig = updatedColumns.reduce((acc, col) => {
                    acc[col.id] = col;
                    return acc;
                }, {});
                const { error } = await supabase
                    .from('boards')
                    .update({
                    columns_config: columnsConfig,
                    column_order: updatedColumns.map(c => c.id),
                    available_columns: updatedColumns.map(c => c.id)
                })
                    .eq('id', boardInfo.id);
                if (error) {
                    console.error('Error saving column to Supabase:', error);
                    // Revert on error
                    setColumns(prev => prev.filter(c => c.id !== newColumn.id));
                }
                else {
                    console.log('Column saved successfully to Supabase');
                }
            }
            catch (error) {
                console.error('Failed to save column:', error);
                // Revert on error
                setColumns(prev => prev.filter(c => c.id !== newColumn.id));
            }
        }
    };
    const handleSaveColumnName = async (columnId, newName) => {
        const column = columns.find(c => c.id === columnId);
        if (!column)
            return;
        // Update local state first
        setColumns(prev => prev.map(c => c.id === columnId ? { ...c, name: newName } : c));
        setShowColumnNameEditor(false);
        // Save to Supabase
        if (boardId && boardInfo) {
            try {
                const updatedColumns = columns.map(c => c.id === columnId ? { ...c, name: newName } : c);
                const columnsConfig = updatedColumns.reduce((acc, col) => {
                    acc[col.id] = col;
                    return acc;
                }, {});
                const { error } = await supabase
                    .from('boards')
                    .update({
                    columns_config: columnsConfig,
                })
                    .eq('id', boardInfo.id);
                if (error) {
                    console.error('Error saving column name to Supabase:', error);
                    // Revert on error
                    setColumns(prev => prev.map(c => c.id === columnId ? { ...c, name: column.name } : c));
                }
                else {
                    console.log('Column name saved successfully to Supabase');
                }
            }
            catch (error) {
                console.error('Failed to save column name:', error);
                // Revert on error
                setColumns(prev => prev.map(c => c.id === columnId ? { ...c, name: column.name } : c));
            }
        }
    };
    const handleEditNumbers = (columnId) => {
        const column = columns.find(c => c.id === columnId);
        if (column && column.type === 'numbers') {
            setEditingColumnForSettings(column);
            setShowColumnSettingsModal(true);
            console.log('[Edit Numbers Column]', { columnId, name: column.name });
        }
    };
    const handleSaveColumnSettings = async (updatedColumn) => {
        if (!boardId || !boardInfo)
            return;
        try {
            // Update local state
            setColumns(prev => prev.map(col => (col.id === updatedColumn.id ? updatedColumn : col)));
            // Save to Supabase
            const updatedColumns = columns.map(col => col.id === updatedColumn.id ? updatedColumn : col);
            const columnsConfig = updatedColumns.reduce((acc, col) => {
                acc[col.id] = col;
                return acc;
            }, {});
            const { error } = await supabase
                .from('boards')
                .update({
                columns_config: columnsConfig,
            })
                .eq('id', boardInfo.id);
            if (error) {
                console.error('Error saving column settings to Supabase:', error);
                throw error;
            }
            console.log('[Column Settings Saved to Supabase]', {
                columnId: updatedColumn.id,
                name: updatedColumn.name,
                type: updatedColumn.type,
            });
        }
        catch (error) {
            console.error('Failed to save column settings:', error);
            throw error;
        }
    };
    const handleDeleteColumn = async (columnId) => {
        const column = columns.find(c => c.id === columnId);
        if (!column)
            return;
        // Remove from local state first
        setColumns(prev => prev.filter(c => c.id !== columnId));
        setShowStatusEditor(false);
        setShowColumnSettingsModal(false);
        // Save to Supabase
        if (boardId && boardInfo) {
            try {
                const updatedColumns = columns.filter(c => c.id !== columnId);
                const columnsConfig = updatedColumns.reduce((acc, col) => {
                    acc[col.id] = col;
                    return acc;
                }, {});
                const { error } = await supabase
                    .from('boards')
                    .update({
                    columns_config: columnsConfig,
                })
                    .eq('id', boardInfo.id);
                if (error) {
                    console.error('Error deleting column from Supabase:', error);
                    // Revert on error
                    setColumns(prev => [...prev, column]);
                }
                else {
                    console.log('Column deleted successfully from Supabase');
                }
            }
            catch (error) {
                console.error('Failed to delete column:', error);
                // Revert on error
                setColumns(prev => [...prev, column]);
            }
        }
    };
    const handleDeleteItem = async (itemId) => {
        if (window.confirm('Delete this item?')) {
            const item = items.find(i => i.id === itemId);
            // Update local state first
            setItems(prev => prev.filter(item => item.id !== itemId));
            // Delete from Supabase
            if (boardId && item) {
                try {
                    await boardSupabaseService.deleteTask(boardId, item.groupId, itemId);
                    console.log('Item deleted successfully from Supabase');
                }
                catch (error) {
                    console.error('Failed to delete item from Supabase:', error);
                    // Revert on error
                    setItems(prev => [...prev, item]);
                }
            }
        }
    };
    const handleBulkEdit = async (columnId, value) => {
        // Update all selected items with the new value
        const updatedItems = items.map(item => {
            if (selectedItems.includes(item.id)) {
                return {
                    ...item,
                    data: {
                        ...item.data,
                        [columnId]: value,
                    },
                };
            }
            return item;
        });
        setItems(updatedItems);
        setShowBulkEditModal(false);
        // Save to Supabase
        if (boardId) {
            try {
                for (const itemId of selectedItems) {
                    const item = updatedItems.find(i => i.id === itemId);
                    if (item) {
                        await boardSupabaseService.updateTask(boardId, item.groupId, itemId, {
                            data: {
                                ...item.data,
                                [columnId]: value,
                            },
                        });
                    }
                }
                console.log('Bulk edit applied successfully');
            }
            catch (error) {
                console.error('Failed to apply bulk edit:', error);
                // Revert on error
                setItems(items);
            }
        }
    };
    const handleAddGroup = async () => {
        const name = prompt('Group name:');
        if (name && boardId) {
            const colors = ['#0073ea', '#00c875', '#ff5e5b', '#fdab3d', '#579bfc'];
            const newGroup = {
                id: uuid(),
                name,
                color: colors[groups.length % colors.length],
                position: groups.length,
                collapsed: false,
            };
            // First update local state
            setGroups(prev => [...prev, newGroup]);
            // Then save to Supabase
            try {
                await boardSupabaseService.createGroup(boardId, {
                    title: name,
                    color: newGroup.color,
                    position: newGroup.position,
                    isCollapsed: false
                });
                console.log('Group created successfully in Supabase');
            }
            catch (error) {
                console.error('Failed to create group in Supabase:', error);
                // Revert local state on error
                setGroups(prev => prev.filter(g => g.id !== newGroup.id));
            }
        }
    };
    const handleEditGroup = (group) => {
        setEditingGroup(group);
        setShowGroupEditModal(true);
    };
    const handleSaveGroup = async (updatedGroup) => {
        if (!boardId)
            return;
        // Update local state first
        setGroups(prev => prev.map(g => (g.id === updatedGroup.id ? updatedGroup : g)));
        // Then save to Supabase
        try {
            await boardSupabaseService.updateGroup(boardId, updatedGroup.id, {
                title: updatedGroup.name,
                color: updatedGroup.color,
            });
            console.log('[Group Saved] Group updated in Supabase:', {
                groupId: updatedGroup.id,
                name: updatedGroup.name,
                color: updatedGroup.color,
            });
        }
        catch (error) {
            console.error('Failed to save group to Supabase:', error);
            // Revert on error
            const originalGroup = groups.find(g => g.id === updatedGroup.id);
            if (originalGroup) {
                setGroups(prev => prev.map(g => (g.id === updatedGroup.id ? originalGroup : g)));
            }
            throw error;
        }
    };
    const handleDeleteGroup = async (groupId) => {
        if (window.confirm('Delete this group and all its items?')) {
            // Store the group and items for potential revert
            const deletedGroup = groups.find(g => g.id === groupId);
            const deletedItems = items.filter(item => item.groupId === groupId);
            // First update local state
            setItems(prev => prev.filter(item => item.groupId !== groupId));
            setGroups(prev => prev.filter(group => group.id !== groupId));
            // Then delete from Supabase
            try {
                await boardSupabaseService.deleteGroup(boardId || '', groupId);
                console.log('Group deleted successfully from Supabase');
            }
            catch (error) {
                console.error('Failed to delete group from Supabase:', error);
                // Revert on error
                if (deletedGroup) {
                    setGroups(prev => [...prev, deletedGroup].sort((a, b) => a.position - b.position));
                }
                if (deletedItems.length > 0) {
                    setItems(prev => [...prev, ...deletedItems]);
                }
            }
        }
    };
    const handleDragStart = (event) => {
        setActiveId(event.active.id);
    };
    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (!over) {
            setActiveId(null);
            return;
        }
        if (active.id !== over.id) {
            // Check if we're dragging a column
            const activeColumn = columns.find(col => col.id === active.id);
            const overColumn = columns.find(col => col.id === over.id);
            if (activeColumn && overColumn) {
                // Handle column reordering
                setColumns(prevColumns => {
                    const oldIndex = prevColumns.findIndex(col => col.id === active.id);
                    const newIndex = prevColumns.findIndex(col => col.id === over.id);
                    if (oldIndex === -1 || newIndex === -1)
                        return prevColumns;
                    const newColumns = [...prevColumns];
                    const [movedColumn] = newColumns.splice(oldIndex, 1);
                    newColumns.splice(newIndex, 0, movedColumn);
                    // Update positions
                    newColumns.forEach((column, index) => {
                        column.position = index;
                    });
                    return newColumns;
                });
            }
            else {
                // Handle item dragging
                const activeItem = items.find(item => item.id === active.id);
                if (activeItem) {
                    // Check if dropping on a group header (cross-group move)
                    const overIdString = over.id;
                    if (overIdString.startsWith('group-')) {
                        const targetGroupId = overIdString.replace('group-', '');
                        if (activeItem.groupId !== targetGroupId) {
                            // Move item to the end of the target group
                            setItems(prevItems => {
                                const newItems = [...prevItems];
                                const itemIndex = newItems.findIndex(item => item.id === activeItem.id);
                                if (itemIndex !== -1) {
                                    newItems[itemIndex] = { ...activeItem, groupId: targetGroupId };
                                }
                                return newItems;
                            });
                        }
                    }
                    else {
                        // Handle item-to-item reordering within the same group or cross-group
                        const overItem = items.find(item => item.id === over.id);
                        if (overItem && activeItem.id !== overItem.id) {
                            setItems(prevItems => {
                                const newItems = [...prevItems];
                                const activeIndex = newItems.findIndex(item => item.id === active.id);
                                const overIndex = newItems.findIndex(item => item.id === over.id);
                                if (activeIndex === -1 || overIndex === -1)
                                    return prevItems;
                                // Remove the active item
                                const [movedItem] = newItems.splice(activeIndex, 1);
                                // If moving to a different group, update the groupId
                                if (activeItem.groupId !== overItem.groupId) {
                                    movedItem.groupId = overItem.groupId;
                                }
                                // Calculate new index after removal (if overIndex was after activeIndex)
                                const adjustedOverIndex = overIndex > activeIndex ? overIndex - 1 : overIndex;
                                // Insert at the new position
                                newItems.splice(adjustedOverIndex, 0, movedItem);
                                // Update positions for all items in affected groups
                                const affectedGroups = [...new Set([activeItem.groupId, overItem.groupId])];
                                affectedGroups.forEach(groupId => {
                                    const groupItems = newItems.filter(item => item.groupId === groupId);
                                    // Sort by their current order in the array
                                    groupItems.sort((a, b) => {
                                        return newItems.indexOf(a) - newItems.indexOf(b);
                                    });
                                    // Update positions
                                    groupItems.forEach((item, index) => {
                                        item.position = index;
                                    });
                                });
                                return newItems;
                            });
                        }
                    }
                }
            }
        }
        setActiveId(null);
    };
    // Add Column Modal
    const AddColumnModal = () => {
        const [columnName, setColumnName] = useState('');
        const [selectedType, setSelectedType] = useState('text');
        const columnTypes = [
            { type: 'text', label: 'Text', icon: Type, description: 'Simple text field' },
            { type: 'status', label: 'Status', icon: CheckCircle2, description: 'Status with colors' },
            { type: 'person', label: 'Person', icon: Users, description: 'Assign team members' },
            { type: 'date', label: 'Date', icon: Calendar, description: 'Date selection' },
            { type: 'checkbox', label: 'Checkbox', icon: Check, description: 'Yes/No toggle' },
            { type: 'progress', label: 'Progress', icon: BarChart3, description: 'Progress bar' },
            { type: 'files', label: 'Files', icon: FileText, description: 'Attach files or links' },
            { type: 'timeline', label: 'Timeline', icon: Calendar, description: 'Start/end dates with progress' },
            { type: 'numbers', label: 'Numbers', icon: Hash, description: 'Formatted numbers' },
            { type: 'time-tracker', label: 'Time Tracker', icon: Clock, description: 'Track time spent on items' }
        ];
        const handleSubmit = (e) => {
            e.preventDefault();
            if (!columnName.trim())
                return;
            handleAddColumn({ name: columnName, type: selectedType });
            setColumnName('');
            setSelectedType('text');
        };
        if (!showAddColumnModal)
            return null;
        return (_jsx("div", { className: "fixed inset-0 bg-black/50 flex items-center justify-center z-50", onClick: () => setShowAddColumnModal(false), children: _jsxs("div", { className: "bg-gray-800 border border-white/20 shadow-2xl rounded-xl p-8 w-full max-w-2xl max-h-[80vh] overflow-y-auto", onClick: (e) => e.stopPropagation(), children: [_jsxs("div", { className: "mb-6", children: [_jsx("h2", { className: "text-2xl font-bold text-white mb-2", children: "Add Column" }), _jsx("p", { className: "text-gray-400 text-sm", children: "Choose a column type and give it a name" })] }), _jsxs("form", { onSubmit: handleSubmit, children: [_jsxs("div", { className: "mb-8", children: [_jsx("label", { className: "block text-sm font-semibold text-gray-300 mb-3", children: "Column Name" }), _jsx("input", { type: "text", value: columnName, onChange: (e) => setColumnName(e.target.value), className: "w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/50 transition-all", placeholder: "e.g., Priority, Assignee, Tags...", autoFocus: true })] }), _jsxs("div", { className: "mb-8", children: [_jsx("label", { className: "block text-sm font-semibold text-gray-300 mb-4", children: "Column Type" }), _jsx("div", { className: "grid grid-cols-2 md:grid-cols-5 gap-3", children: columnTypes.map(({ type, label, icon: Icon, description }) => (_jsxs("button", { type: "button", onClick: () => setSelectedType(type), className: `relative p-4 rounded-lg border-2 transition-all group ${selectedType === type
                                                ? 'border-blue-500 bg-blue-500/10'
                                                : 'border-gray-600 bg-gray-700/50 hover:border-gray-500 hover:bg-gray-700'}`, title: description, children: [_jsxs("div", { className: "flex flex-col items-center gap-2", children: [_jsx(Icon, { className: `w-6 h-6 ${selectedType === type ? 'text-blue-400' : 'text-gray-400 group-hover:text-gray-300'}` }), _jsx("span", { className: `text-xs font-medium text-center ${selectedType === type ? 'text-blue-400' : 'text-gray-300'}`, children: label })] }), selectedType === type && (_jsx("div", { className: "absolute top-2 right-2 w-2 h-2 bg-blue-400 rounded-full" }))] }, type))) })] }), _jsxs("div", { className: "flex justify-end gap-3", children: [_jsx("button", { type: "button", onClick: () => setShowAddColumnModal(false), className: "px-6 py-2.5 text-gray-300 hover:text-white bg-transparent hover:bg-gray-700 rounded-lg transition-colors font-medium", children: "Cancel" }), _jsx("button", { type: "submit", disabled: !columnName.trim(), className: "px-6 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors font-medium", children: "Add Column" })] })] })] }) }));
    };
    return (_jsxs(_Fragment, { children: [_jsx(ConfettiAnimation, { trigger: triggerConfetti }), _jsxs(DndContext, { sensors: sensors, onDragStart: handleDragStart, onDragEnd: handleDragEnd, collisionDetection: closestCenter, children: [_jsxs("div", { className: `flex flex-col h-full rounded-xl overflow-hidden ${isDark
                            ? 'bg-[#0f1d2e]'
                            : 'bg-white'}`, style: {
                            boxShadow: isDark
                                ? '0 4px 24px rgba(0, 0, 0, 0.4)'
                                : '0 1px 3px rgba(0, 0, 0, 0.05), 0 1px 2px rgba(0, 0, 0, 0.02)'
                        }, children: [_jsxs("div", { className: `flex items-center justify-between px-8 py-3 border-b ${isDark
                                    ? 'bg-[#0f1d2e] border-[#0ea5e9]/10'
                                    : 'bg-white border-[#00A5CF]/10'}`, children: [_jsxs("div", { className: "flex items-center gap-2", children: [_jsxs("button", { onClick: () => setShowSidekickModal(!showSidekickModal), className: `flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${isDark
                                                    ? 'text-gray-300 hover:text-white hover:bg-[#0a1628]'
                                                    : 'text-gray-700 hover:text-black hover:bg-gray-100'}`, title: "Open Sidekick", children: [_jsx(Sparkles, { className: "w-4 h-4" }), _jsx("span", { children: "Sidekick" })] }), _jsxs("button", { onClick: () => setShowIntegrateModal(!showIntegrateModal), className: `flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${isDark
                                                    ? 'text-gray-300 hover:text-white hover:bg-[#0a1628]'
                                                    : 'text-gray-700 hover:text-black hover:bg-gray-100'}`, title: "Open Integrate", children: [_jsx(Link, { className: "w-4 h-4" }), _jsx("span", { children: "Integrate" })] }), _jsxs("button", { onClick: () => setShowAutomation(!showAutomation), className: `flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${isDark
                                                    ? 'text-yellow-300 hover:text-yellow-100 hover:bg-yellow-500/10'
                                                    : 'text-yellow-700 hover:text-yellow-900 hover:bg-yellow-100'}`, title: "Open Automation", children: [_jsx(Zap, { className: "w-4 h-4" }), _jsx("span", { children: "Automate" })] }), _jsxs("button", { className: `flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${isDark
                                                    ? 'text-gray-300 hover:text-white hover:bg-[#0a1628]'
                                                    : 'text-gray-700 hover:text-black hover:bg-gray-100'}`, title: "Open Comments", children: [_jsx(MessageSquare, { className: "w-4 h-4" }), _jsx("span", { children: "Comments" })] })] }), _jsxs("div", { className: "flex items-center gap-2 relative", children: [_jsxs("div", { className: "relative group", children: [_jsx("button", { onClick: () => setShowActivityLog(true), className: `w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-all ${isDark
                                                            ? 'bg-[#0ea5e9] text-white hover:bg-[#38bdf8]'
                                                            : 'bg-[#00A5CF] text-white hover:bg-[#0891b2]'}`, title: currentUser?.full_name || 'User', children: currentUser?.full_name
                                                            ? currentUser.full_name.charAt(0).toUpperCase()
                                                            : '?' }), _jsx("div", { className: `absolute bottom-full right-0 mb-2 px-2 py-1 rounded text-xs whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 ${isDark
                                                            ? 'bg-[#333333] text-white'
                                                            : 'bg-gray-800 text-white'}`, children: "View activity log" })] }), _jsx("button", { className: `p-2 rounded-lg transition-all ${isDark
                                                    ? 'text-gray-400 hover:text-white hover:bg-[#0a1628]'
                                                    : 'text-gray-600 hover:text-black hover:bg-gray-100'}`, title: "Sync / Refresh", children: _jsx(RefreshCw, { className: "w-4 h-4" }) }), _jsxs("button", { onClick: () => setShowInviteModal(true), className: `flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${isDark
                                                    ? 'text-gray-300 hover:text-white hover:bg-[#0a1628]'
                                                    : 'text-gray-700 hover:text-black hover:bg-gray-100'}`, title: "Invite members", children: [_jsx(Users, { className: "w-4 h-4" }), _jsx("span", { children: "Invite" }), boardMembers.length > 0 && (_jsx("span", { className: "text-xs px-1.5 py-0.5 rounded-full", style: {
                                                            backgroundColor: isDark ? '#0ea5e9/20' : '#00A5CF/20',
                                                            color: isDark ? '#0ea5e9' : '#00A5CF'
                                                        }, children: boardMembers.length }))] }), _jsxs("div", { className: "relative", children: [_jsx("button", { onClick: () => setShowMenuDropdown(!showMenuDropdown), className: `p-2 rounded-lg transition-all ${isDark
                                                            ? 'text-gray-400 hover:text-white hover:bg-[#0a1628]'
                                                            : 'text-gray-600 hover:text-black hover:bg-gray-100'}`, title: "More options", children: _jsx(MoreVertical, { className: "w-4 h-4" }) }), showMenuDropdown && (_jsxs("div", { className: `absolute right-0 mt-2 w-48 rounded-lg shadow-lg border z-50 ${isDark
                                                            ? 'bg-[#1a1a1a] border-[#333333]'
                                                            : 'bg-white border-gray-200'}`, children: [_jsx("button", { onClick: () => {
                                                                    setShowActivityLog(true);
                                                                    setShowMenuDropdown(false);
                                                                }, className: `w-full text-left px-4 py-2 text-sm transition-colors ${isDark
                                                                    ? 'text-gray-300 hover:bg-[#0f1d2e]'
                                                                    : 'text-gray-700 hover:bg-gray-100'}`, children: "Activity log" }), _jsx("button", { className: `w-full text-left px-4 py-2 text-sm transition-colors ${isDark
                                                                    ? 'text-gray-300 hover:bg-[#0f1d2e]'
                                                                    : 'text-gray-700 hover:bg-gray-100'}`, title: "Coming soon", children: "Power-Ups" }), _jsx("button", { className: `w-full text-left px-4 py-2 text-sm transition-colors ${isDark
                                                                    ? 'text-gray-300 hover:bg-[#0f1d2e]'
                                                                    : 'text-gray-700 hover:bg-gray-100'}`, title: "Coming soon", children: "Discussions" }), _jsx("button", { onClick: () => {
                                                                    setShowNotificationsModal(true);
                                                                    setShowMenuDropdown(false);
                                                                }, className: `w-full text-left px-4 py-2 text-sm transition-colors ${isDark
                                                                    ? 'text-gray-300 hover:bg-[#0f1d2e]'
                                                                    : 'text-gray-700 hover:bg-gray-100'}`, children: "Notifications" }), _jsx("button", { className: `w-full text-left px-4 py-2 text-sm transition-colors ${isDark
                                                                    ? 'text-gray-300 hover:bg-[#0f1d2e]'
                                                                    : 'text-gray-700 hover:bg-gray-100'}`, title: "Coming soon", children: "Permissions" }), _jsx("button", { className: `w-full text-left px-4 py-2 text-sm transition-colors ${isDark
                                                                    ? 'text-gray-300 hover:bg-[#0f1d2e]'
                                                                    : 'text-gray-700 hover:bg-gray-100'}`, title: "Coming soon", children: "Settings" }), _jsx("button", { className: `w-full text-left px-4 py-2 text-sm transition-colors ${isDark
                                                                    ? 'text-gray-300 hover:bg-[#0f1d2e]'
                                                                    : 'text-gray-700 hover:bg-gray-100'}`, title: "Coming soon", children: "View archive" }), _jsx("hr", { className: isDark ? 'border-[#333333]' : 'border-gray-200' }), _jsxs("button", { onClick: () => setShowMoreActionsSubmenu(!showMoreActionsSubmenu), className: `w-full text-left px-4 py-2 text-sm transition-colors flex items-center justify-between relative ${isDark
                                                                    ? 'text-gray-300 hover:bg-[#0f1d2e]'
                                                                    : 'text-gray-700 hover:bg-gray-100'}`, title: "More options", children: ["More actions", _jsx("span", { className: "text-xs", children: "\u203A" })] }), showMoreActionsSubmenu && (_jsxs("div", { className: `absolute left-0 top-0 w-48 rounded-lg shadow-lg border z-50 ml-48 ${isDark
                                                                    ? 'bg-[#1a1a1a] border-[#333333]'
                                                                    : 'bg-white border-gray-200'}`, children: [_jsx("button", { onClick: () => {
                                                                            setShowImportModal(true);
                                                                            setShowMenuDropdown(false);
                                                                            setShowMoreActionsSubmenu(false);
                                                                        }, className: `w-full text-left px-4 py-2 text-sm transition-colors ${isDark
                                                                            ? 'text-green-400 hover:bg-green-500/10'
                                                                            : 'text-green-600 hover:bg-green-100'}`, children: "Import items" }), _jsx("button", { className: `w-full text-left px-4 py-2 text-sm transition-colors ${isDark
                                                                            ? 'text-orange-400 hover:bg-orange-500/10'
                                                                            : 'text-orange-600 hover:bg-orange-100'}`, title: "Coming soon", children: "Archive board" }), _jsx("button", { className: `w-full text-left px-4 py-2 text-sm transition-colors ${isDark
                                                                            ? 'text-gray-300 hover:bg-[#0f1d2e]'
                                                                            : 'text-gray-700 hover:bg-gray-100'}`, title: "Coming soon", children: "Duplicate board" }), _jsx("button", { className: `w-full text-left px-4 py-2 text-sm transition-colors ${isDark
                                                                            ? 'text-gray-300 hover:bg-[#0f1d2e]'
                                                                            : 'text-gray-700 hover:bg-gray-100'}`, title: "Coming soon", children: "Export data" }), _jsx("button", { className: `w-full text-left px-4 py-2 text-sm transition-colors ${isDark
                                                                            ? 'text-blue-400 hover:bg-blue-500/10'
                                                                            : 'text-blue-600 hover:bg-blue-100'}`, title: "Coming soon", children: "Give feedback" })] })), _jsx("button", { className: `w-full text-left px-4 py-2 text-sm transition-colors ${isDark
                                                                    ? 'text-red-400 hover:bg-red-500/10'
                                                                    : 'text-red-600 hover:bg-red-100'}`, title: "Delete this board permanently", children: "Delete board" })] }))] })] })] }), _jsxs("div", { className: `flex items-start justify-between px-8 py-6 border-b ${isDark
                                    ? 'bg-[#0a1628] border-[#0ea5e9]/10'
                                    : 'bg-gray-50 border-[#00A5CF]/10'}`, children: [_jsx(BoardHeader, { boardId: boardId || '', title: editTitle, description: editDescription, onTitleChange: handleTitleChange, onDescriptionChange: handleDescriptionChange, onOpenBoardInfo: () => setShowBoardInfo(true), isDark: isDark }), _jsxs("div", { className: "flex items-center gap-3", children: [_jsxs("button", { onClick: () => setShowMooPanel(!showMooPanel), className: "flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-all", style: {
                                                    borderColor: isDark ? '#0ea5e9' : '#00A5CF',
                                                    backgroundColor: isDark ? '#0ea5e9/10' : '#00A5CF/10',
                                                    color: isDark ? '#0ea5e9' : '#00A5CF'
                                                }, title: "Open Moo AI Assistant", children: [_jsx("span", { className: "text-lg", children: "\uD83D\uDC04" }), _jsx("span", { children: "Moo" }), _jsx("span", { className: "text-xs px-2 py-0.5 rounded-full", style: {
                                                            backgroundColor: isDark ? '#0ea5e9/20' : '#00A5CF/20',
                                                        }, children: "Beta" })] }), _jsxs("button", { onClick: handleAddGroup, className: `flex items-center gap-2 px-4 py-2.5 border rounded-lg text-sm font-medium transition-all ${isDark
                                                    ? 'border-[#0ea5e9]/20 bg-[#0f1d2e] text-[#38bdf8] hover:border-[#0ea5e9]/40 hover:bg-[#0a1628]'
                                                    : 'border-[#00A5CF]/20 bg-white text-[#00A5CF] hover:border-[#00A5CF]/40 hover:bg-gray-100'}`, children: [_jsx(Plus, { className: "w-4 h-4" }), _jsx("span", { children: "New Group" })] }), _jsxs("button", { onClick: () => groups.length > 0 && handleAddItem(groups[0].id), className: `flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${isDark
                                                    ? 'bg-[#0ea5e9] text-white hover:bg-[#38bdf8]'
                                                    : 'bg-[#00A5CF] text-white hover:bg-[#0891b2]'}`, style: {
                                                    boxShadow: isDark
                                                        ? '0 2px 8px rgba(14, 165, 233, 0.2)'
                                                        : '0 1px 3px rgba(0, 165, 207, 0.2)'
                                                }, children: [_jsx(Plus, { className: "w-4 h-4" }), _jsx("span", { children: "New Item" })] }), _jsxs("button", { onClick: () => setShowSaveViewModal(true), className: `flex items-center gap-2 px-4 py-2.5 border rounded-lg text-sm font-medium transition-all ${isDark
                                                    ? 'border-[#10b981]/20 bg-[#0f1d2e] text-[#6B8E6F] hover:border-[#10b981]/40 hover:bg-[#0a1628]'
                                                    : 'border-[#6B8E6F]/20 bg-white text-[#6B8E6F] hover:border-[#6B8E6F]/40 hover:bg-gray-100'}`, children: [_jsx(Settings, { className: "w-4 h-4" }), _jsx("span", { children: "Save View" })] }), _jsxs("button", { onClick: () => setShowAutomation(!showAutomation), className: `flex items-center gap-2 px-4 py-2.5 border rounded-lg text-sm font-medium transition-all ${isDark
                                                    ? 'border-[#fbbf24]/20 bg-[#0f1d2e] text-[#fbbf24] hover:border-[#fbbf24]/40 hover:bg-[#0a1628]'
                                                    : 'border-[#d97706]/20 bg-white text-[#d97706] hover:border-[#d97706]/40 hover:bg-gray-100'}`, children: [_jsx(Zap, { className: "w-4 h-4" }), _jsx("span", { children: "Automate" })] })] })] }), _jsxs("div", { className: `flex items-center justify-between px-6 py-3 border-b ${isDark
                                    ? 'bg-[#0a1628] border-[#0ea5e9]/10'
                                    : 'bg-gray-50 border-[#00A5CF]/10'}`, children: [_jsxs("div", { className: "flex items-center gap-1", children: [_jsxs("button", { onClick: () => setCurrentView('table'), className: `flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-light transition-all ${currentView === 'table'
                                                    ? isDark
                                                        ? 'bg-[#0ea5e9]/10 text-[#38bdf8] border border-[#0ea5e9]/20'
                                                        : 'bg-[#00A5CF]/10 text-[#00A5CF] border border-[#00A5CF]/20'
                                                    : isDark
                                                        ? 'text-gray-400 hover:text-[#38bdf8] hover:bg-[#0f1d2e]'
                                                        : 'text-gray-600 hover:text-[#00A5CF] hover:bg-white'}`, children: [_jsx(Table2, { className: "w-4 h-4" }), _jsx("span", { children: "Table" })] }), _jsxs("button", { onClick: () => setCurrentView('kanban'), className: `flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-light transition-all ${currentView === 'kanban'
                                                    ? isDark
                                                        ? 'bg-[#0ea5e9]/10 text-[#38bdf8] border border-[#0ea5e9]/20'
                                                        : 'bg-[#00A5CF]/10 text-[#00A5CF] border border-[#00A5CF]/20'
                                                    : isDark
                                                        ? 'text-gray-400 hover:text-[#38bdf8] hover:bg-[#0f1d2e]'
                                                        : 'text-gray-600 hover:text-[#00A5CF] hover:bg-white'}`, children: [_jsx(Columns3, { className: "w-4 h-4" }), _jsx("span", { children: "Kanban" })] }), _jsxs("button", { onClick: () => setCurrentView('calendar'), className: `flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-light transition-all ${currentView === 'calendar'
                                                    ? isDark
                                                        ? 'bg-[#0ea5e9]/10 text-[#38bdf8] border border-[#0ea5e9]/20'
                                                        : 'bg-[#00A5CF]/10 text-[#00A5CF] border border-[#00A5CF]/20'
                                                    : isDark
                                                        ? 'text-gray-400 hover:text-[#38bdf8] hover:bg-[#0f1d2e]'
                                                        : 'text-gray-600 hover:text-[#00A5CF] hover:bg-white'}`, children: [_jsx(Calendar, { className: "w-4 h-4" }), _jsx("span", { children: "Calendar" })] }), _jsxs("button", { onClick: () => setCurrentView('timeline'), className: `flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-light transition-all ${currentView === 'timeline'
                                                    ? isDark
                                                        ? 'bg-[#0ea5e9]/10 text-[#38bdf8] border border-[#0ea5e9]/20'
                                                        : 'bg-[#00A5CF]/10 text-[#00A5CF] border border-[#00A5CF]/20'
                                                    : isDark
                                                        ? 'text-gray-400 hover:text-[#38bdf8] hover:bg-[#0f1d2e]'
                                                        : 'text-gray-600 hover:text-[#00A5CF] hover:bg-white'}`, children: [_jsx(Clock, { className: "w-4 h-4" }), _jsx("span", { children: "Timeline" })] }), _jsxs("button", { onClick: () => setCurrentView('gantt'), className: `flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-light transition-all ${currentView === 'gantt'
                                                    ? isDark
                                                        ? 'bg-[#0ea5e9]/10 text-[#38bdf8] border border-[#0ea5e9]/20'
                                                        : 'bg-[#00A5CF]/10 text-[#00A5CF] border border-[#00A5CF]/20'
                                                    : isDark
                                                        ? 'text-gray-400 hover:text-[#38bdf8] hover:bg-[#0f1d2e]'
                                                        : 'text-gray-600 hover:text-[#00A5CF] hover:bg-white'}`, children: [_jsx(BarChart3, { className: "w-4 h-4" }), _jsx("span", { children: "Gantt" })] }), _jsxs("button", { onClick: () => setCurrentView('app'), className: `flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-light transition-all ${currentView === 'app'
                                                    ? isDark
                                                        ? 'bg-[#0ea5e9]/10 text-[#38bdf8] border border-[#0ea5e9]/20'
                                                        : 'bg-[#00A5CF]/10 text-[#00A5CF] border border-[#00A5CF]/20'
                                                    : isDark
                                                        ? 'text-gray-400 hover:text-[#38bdf8] hover:bg-[#0f1d2e]'
                                                        : 'text-gray-600 hover:text-[#00A5CF] hover:bg-white'}`, children: [_jsx(Sparkles, { className: "w-4 h-4" }), _jsx("span", { children: "App" })] })] }), _jsxs("div", { className: `text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`, children: [processedItems.length, " of ", items.length, " items across ", dynamicGroups.length, " groups"] })] }), showBulkActions && (_jsxs("div", { className: `flex items-center justify-between px-6 py-3 border-b ${isDark
                                    ? 'bg-[#0a1628] border-[#0ea5e9]/20'
                                    : 'bg-[#F5F3F0] border-[#00A5CF]/20'}`, children: [_jsxs("div", { className: "flex items-center gap-4", children: [_jsxs("span", { className: `text-sm font-light ${isDark ? 'text-[#38bdf8]' : 'text-[#00A5CF]'}`, children: [selectedItems.length, " item", selectedItems.length !== 1 ? 's' : '', " selected"] }), _jsxs("div", { className: "flex items-center gap-2", children: [_jsxs("button", { onClick: () => setShowBulkEditModal(true), className: `flex items-center gap-1.5 px-3 py-1.5 border rounded-lg text-sm font-light transition-all ${isDark
                                                            ? 'bg-[#0f1d2e] border-[#0ea5e9]/20 text-[#38bdf8] hover:border-[#0ea5e9]/40'
                                                            : 'bg-white border-[#00A5CF]/20 text-[#00A5CF] hover:border-[#00A5CF]/40'}`, children: [_jsx(Edit, { className: "w-4 h-4" }), _jsx("span", { children: "Bulk Edit" })] }), _jsxs("button", { onClick: handleBulkDuplicate, className: `flex items-center gap-1.5 px-3 py-1.5 border rounded-lg text-sm font-light transition-all ${isDark
                                                            ? 'bg-[#0f1d2e] border-[#0ea5e9]/20 text-[#38bdf8] hover:border-[#0ea5e9]/40'
                                                            : 'bg-white border-[#00A5CF]/20 text-[#00A5CF] hover:border-[#00A5CF]/40'}`, children: [_jsx(Copy, { className: "w-4 h-4" }), _jsx("span", { children: "Duplicate" })] }), _jsxs("button", { onClick: handleBulkArchive, className: `flex items-center gap-1.5 px-3 py-1.5 border rounded-lg text-sm font-light transition-all ${isDark
                                                            ? 'bg-[#0f1d2e] border-[#0ea5e9]/20 text-[#38bdf8] hover:border-[#0ea5e9]/40'
                                                            : 'bg-white border-[#00A5CF]/20 text-[#00A5CF] hover:border-[#00A5CF]/40'}`, children: [_jsx(Archive, { className: "w-4 h-4" }), _jsx("span", { children: "Archive" })] }), _jsxs("button", { onClick: handleBulkDelete, className: `flex items-center gap-1.5 px-3 py-1.5 border rounded-lg text-sm font-light transition-all ${isDark
                                                            ? 'bg-[#0f1d2e] border-red-500/20 text-red-400 hover:border-red-500/40'
                                                            : 'bg-white border-red-500/20 text-red-600 hover:border-red-500/40'}`, children: [_jsx(Trash2, { className: "w-4 h-4" }), _jsx("span", { children: "Delete" })] })] })] }), _jsxs("button", { onClick: deselectAllItems, className: `flex items-center gap-1.5 text-sm font-light transition-colors ${isDark
                                            ? 'text-gray-400 hover:text-[#38bdf8]'
                                            : 'text-gray-600 hover:text-[#00A5CF]'}`, children: [_jsx(X, { className: "w-4 h-4" }), _jsx("span", { children: "Clear selection" })] })] })), _jsx("div", { className: `flex items-center justify-between px-6 py-3 border-b ${isDark
                                    ? 'bg-[#0a1628] border-[#0ea5e9]/10'
                                    : 'bg-gray-50 border-[#00A5CF]/10'}`, children: _jsxs("div", { className: "flex items-center gap-3", children: [_jsxs("div", { className: "relative", children: [_jsx(Search, { className: `w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 ${isDark ? 'text-gray-500' : 'text-gray-400'}` }), _jsx("input", { type: "text", placeholder: "Search items...", value: searchQuery, onChange: (e) => setSearchQuery(e.target.value), className: `pl-10 pr-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 transition-all ${isDark
                                                        ? 'bg-[#0f1d2e] border-[#0ea5e9]/10 text-white placeholder:text-gray-500 focus:ring-[#0ea5e9]/30 focus:border-[#0ea5e9]/30'
                                                        : 'bg-white border-[#00A5CF]/10 text-gray-900 placeholder:text-gray-400 focus:ring-[#00A5CF]/30 focus:border-[#00A5CF]/30'}` })] }), _jsxs("button", { onClick: () => setShowFilters(!showFilters), className: `flex items-center gap-2 px-3 py-2 border rounded-lg text-sm font-light transition-all ${showFilters || hasActiveFilters
                                                ? isDark
                                                    ? 'bg-[#0ea5e9]/10 text-[#38bdf8] border-[#0ea5e9]/20'
                                                    : 'bg-[#00A5CF]/10 text-[#00A5CF] border-[#00A5CF]/20'
                                                : isDark
                                                    ? 'bg-[#0f1d2e] text-gray-400 border-[#0ea5e9]/10 hover:text-[#38bdf8] hover:border-[#0ea5e9]/20'
                                                    : 'bg-white text-gray-600 border-[#00A5CF]/10 hover:text-[#00A5CF] hover:border-[#00A5CF]/20'}`, children: [_jsx(Filter, { className: "w-4 h-4" }), _jsx("span", { children: "Filter" }), hasActiveFilters && (_jsx("span", { className: `text-xs rounded-full px-2 py-0.5 ${isDark ? 'bg-[#0ea5e9] text-white' : 'bg-[#00A5CF] text-white'}`, children: Object.keys(activeFilters).length + (searchQuery ? 1 : 0) }))] }), _jsxs("div", { className: "relative", children: [_jsxs("button", { ref: groupByTriggerRef, onClick: () => {
                                                        if (!showGroupByDropdown && groupByTriggerRef.current) {
                                                            const position = calculateGroupByDropdownPosition(groupByTriggerRef.current);
                                                            setGroupByDropdownPosition(position);
                                                        }
                                                        setShowGroupByDropdown(!showGroupByDropdown);
                                                    }, className: `flex items-center gap-2 px-3 py-2 border rounded-lg text-sm font-light transition-all ${groupByColumn
                                                        ? isDark
                                                            ? 'bg-[#6B8E6F]/10 text-[#6B8E6F] border-[#6B8E6F]/20'
                                                            : 'bg-[#6B8E6F]/10 text-[#6B8E6F] border-[#6B8E6F]/20'
                                                        : isDark
                                                            ? 'bg-[#0f1d2e] text-gray-400 border-[#0ea5e9]/10 hover:text-[#38bdf8] hover:border-[#0ea5e9]/20'
                                                            : 'bg-white text-gray-600 border-[#00A5CF]/10 hover:text-[#00A5CF] hover:border-[#00A5CF]/20'}`, children: [_jsx(BarChart3, { className: "w-4 h-4" }), _jsx("span", { children: "Group by" }), groupByColumn && (_jsx("span", { className: "bg-[#6B8E6F] text-white text-xs rounded-full px-2 py-0.5", children: columns.find(col => col.id === groupByColumn)?.name })), _jsx(ChevronDown, { className: "w-3 h-3" })] }), showGroupByDropdown && createPortal(_jsx("div", { ref: groupByDropdownRef, className: `fixed w-64 max-h-96 overflow-y-auto border rounded-lg z-[99998] ${isDark
                                                        ? 'bg-[#0a1628] border-[#0ea5e9]/20'
                                                        : 'bg-white border-[#00A5CF]/20'}`, style: {
                                                        top: `${groupByDropdownPosition.top}px`,
                                                        left: `${groupByDropdownPosition.left}px`,
                                                        boxShadow: isDark
                                                            ? '0 4px 12px rgba(0, 0, 0, 0.3)'
                                                            : '0 2px 8px rgba(0, 0, 0, 0.1)'
                                                    }, children: _jsxs("div", { className: "p-2", children: [_jsx("div", { className: `text-xs font-medium mb-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`, children: "GROUP BY COLUMN" }), _jsx("div", { className: "space-y-1", children: columns.map((column) => (_jsxs("button", { onClick: () => {
                                                                        setGroupByColumn(groupByColumn === column.id ? null : column.id);
                                                                        setShowGroupByDropdown(false);
                                                                    }, className: `w-full flex items-center gap-2 px-2 py-1.5 text-sm text-left rounded-lg transition-all ${groupByColumn === column.id
                                                                        ? isDark
                                                                            ? 'bg-[#6B8E6F]/10 text-[#6B8E6F]'
                                                                            : 'bg-[#6B8E6F]/10 text-[#6B8E6F]'
                                                                        : isDark
                                                                            ? 'text-gray-400 hover:bg-[#0f1d2e] hover:text-white'
                                                                            : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'}`, children: [_jsx("div", { className: `w-2 h-2 rounded-full ${isDark ? 'bg-gray-600' : 'bg-gray-400'}` }), _jsx("span", { children: column.name }), groupByColumn === column.id && _jsx(Check, { className: "w-3 h-3 ml-auto text-[#6B8E6F]" })] }, column.id))) }), groupByColumn && (_jsxs(_Fragment, { children: [_jsxs("div", { className: `border-t mt-2 pt-2 ${isDark ? 'border-[#0ea5e9]/10' : 'border-[#00A5CF]/10'}`, children: [_jsx("div", { className: `text-xs font-medium mb-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`, children: "SORTING" }), _jsxs("div", { className: "space-y-1", children: [_jsxs("button", { onClick: () => setGroupBySorting('asc'), className: `w-full flex items-center gap-2 px-2 py-1.5 text-sm text-left rounded-lg transition-all ${groupBySorting === 'asc'
                                                                                            ? isDark
                                                                                                ? 'bg-[#6B8E6F]/10 text-[#6B8E6F]'
                                                                                                : 'bg-[#6B8E6F]/10 text-[#6B8E6F]'
                                                                                            : isDark
                                                                                                ? 'text-gray-400 hover:bg-[#0f1d2e] hover:text-white'
                                                                                                : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'}`, children: [_jsx("span", { children: "A-Z (ascending)" }), groupBySorting === 'asc' && _jsx(Check, { className: "w-3 h-3 ml-auto text-[#6B8E6F]" })] }), _jsxs("button", { onClick: () => setGroupBySorting('desc'), className: `w-full flex items-center gap-2 px-2 py-1.5 text-sm text-left rounded-lg transition-all ${groupBySorting === 'desc'
                                                                                            ? isDark
                                                                                                ? 'bg-[#6B8E6F]/10 text-[#6B8E6F]'
                                                                                                : 'bg-[#6B8E6F]/10 text-[#6B8E6F]'
                                                                                            : isDark
                                                                                                ? 'text-gray-400 hover:bg-[#0f1d2e] hover:text-white'
                                                                                                : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'}`, children: [_jsx("span", { children: "Z-A (descending)" }), groupBySorting === 'desc' && _jsx(Check, { className: "w-3 h-3 ml-auto text-[#6B8E6F]" })] })] })] }), _jsx("div", { className: `border-t mt-2 pt-2 ${isDark ? 'border-[#0ea5e9]/10' : 'border-[#00A5CF]/10'}`, children: _jsxs("button", { onClick: () => {
                                                                                setGroupByColumn(null);
                                                                                setShowGroupByDropdown(false);
                                                                            }, className: `w-full flex items-center gap-2 px-2 py-1.5 text-sm text-left rounded-lg transition-all ${isDark
                                                                                ? 'text-red-400 hover:bg-red-500/10'
                                                                                : 'text-red-600 hover:bg-red-50'}`, children: [_jsx(X, { className: "w-3 h-3" }), _jsx("span", { children: "Clear grouping" })] }) })] }))] }) }), document.body)] }), hasActiveFilters && (_jsxs("button", { onClick: clearFilters, className: `flex items-center gap-1.5 text-sm font-light transition-colors ${isDark
                                                ? 'text-gray-500 hover:text-red-400'
                                                : 'text-gray-500 hover:text-red-600'}`, children: [_jsx(X, { className: "w-4 h-4" }), _jsx("span", { children: "Clear" })] }))] }) }), showFilters && (_jsx("div", { className: `px-6 py-4 border-b ${isDark
                                    ? 'bg-[#0a1628] border-[#0ea5e9]/10'
                                    : 'bg-gray-50 border-[#00A5CF]/10'}`, children: _jsxs("div", { className: "flex flex-wrap gap-4", children: [_jsxs("div", { children: [_jsx("label", { className: `block text-sm font-light mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`, children: "Status" }), _jsxs("select", { value: activeFilters.status || '', onChange: (e) => setActiveFilters(prev => ({ ...prev, status: e.target.value || undefined })), className: `px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 transition-all ${isDark
                                                        ? 'bg-[#0f1d2e] border-[#0ea5e9]/10 text-white focus:ring-[#0ea5e9]/30 focus:border-[#0ea5e9]/30'
                                                        : 'bg-white border-[#00A5CF]/10 text-gray-900 focus:ring-[#00A5CF]/30 focus:border-[#00A5CF]/30'}`, children: [_jsx("option", { value: "", children: "All statuses" }), columns.find(col => col.id === 'col-2')?.options?.map(option => (_jsx("option", { value: option.id, children: option.label }, option.id)))] })] }), _jsxs("div", { children: [_jsx("label", { className: `block text-sm font-light mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`, children: "Assignee" }), _jsxs("select", { value: activeFilters.person || '', onChange: (e) => setActiveFilters(prev => ({ ...prev, person: e.target.value || undefined })), className: `px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 transition-all ${isDark
                                                        ? 'bg-[#0f1d2e] border-[#0ea5e9]/10 text-white focus:ring-[#0ea5e9]/30 focus:border-[#0ea5e9]/30'
                                                        : 'bg-white border-[#00A5CF]/10 text-gray-900 focus:ring-[#00A5CF]/30 focus:border-[#00A5CF]/30'}`, children: [_jsx("option", { value: "", children: "All people" }), people.map(person => (_jsx("option", { value: person.id, children: person.name }, person.id)))] })] })] }) })), showAutomation && (_jsx("div", { className: `px-6 py-4 border-b ${isDark
                                    ? 'bg-[#0a1628] border-[#0ea5e9]/10'
                                    : 'bg-gray-50 border-[#00A5CF]/10'}`, children: _jsxs("div", { className: "flex items-start gap-4", children: [_jsx(Zap, { className: `w-5 h-5 mt-0.5 ${isDark ? 'text-[#fbbf24]' : 'text-[#d97706]'}` }), _jsxs("div", { className: "flex-1", children: [_jsx("h3", { className: `text-sm font-light mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`, children: "Automation Center" }), _jsxs("div", { className: "space-y-3", children: [_jsx("div", { className: `rounded-lg p-3 border ${isDark
                                                                ? 'bg-[#0f1d2e] border-[#fbbf24]/20'
                                                                : 'bg-white border-[#d97706]/20'}`, children: _jsxs("div", { className: "flex items-center justify-between", children: [_jsxs("div", { children: [_jsx("h4", { className: `text-sm font-light ${isDark ? 'text-white' : 'text-gray-900'}`, children: "Auto-assign on status change" }), _jsx("p", { className: `text-xs mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-600'}`, children: "When status changes to \"Working on it\", assign to John Doe" })] }), _jsx("button", { className: `text-xs px-3 py-1.5 rounded-lg border transition-all ${isDark
                                                                            ? 'bg-[#fbbf24]/10 text-[#fbbf24] border-[#fbbf24]/20 hover:border-[#fbbf24]/40'
                                                                            : 'bg-[#d97706]/10 text-[#d97706] border-[#d97706]/20 hover:border-[#d97706]/40'}`, children: "Set up" })] }) }), _jsx("div", { className: `rounded-lg p-3 border ${isDark
                                                                ? 'bg-[#0f1d2e] border-[#0ea5e9]/20'
                                                                : 'bg-white border-[#00A5CF]/20'}`, children: _jsxs("div", { className: "flex items-center justify-between", children: [_jsxs("div", { children: [_jsx("h4", { className: `text-sm font-light ${isDark ? 'text-white' : 'text-gray-900'}`, children: "Notify when due date approaches" }), _jsx("p", { className: `text-xs mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-600'}`, children: "Send email notification 1 day before due date" })] }), _jsx("button", { className: `text-xs px-3 py-1.5 rounded-lg border transition-all ${isDark
                                                                            ? 'bg-[#0ea5e9]/10 text-[#38bdf8] border-[#0ea5e9]/20 hover:border-[#0ea5e9]/40'
                                                                            : 'bg-[#00A5CF]/10 text-[#00A5CF] border-[#00A5CF]/20 hover:border-[#00A5CF]/40'}`, children: "Set up" })] }) }), _jsx("div", { className: `rounded-lg p-3 border ${isDark
                                                                ? 'bg-[#0f1d2e] border-[#6B8E6F]/20'
                                                                : 'bg-white border-[#6B8E6F]/20'}`, children: _jsxs("div", { className: "flex items-center justify-between", children: [_jsxs("div", { children: [_jsx("h4", { className: `text-sm font-light ${isDark ? 'text-white' : 'text-gray-900'}`, children: "Archive completed items" }), _jsx("p", { className: `text-xs mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-600'}`, children: "Move items to archive 7 days after completion" })] }), _jsx("button", { className: "text-xs bg-[#6B8E6F]/10 text-[#6B8E6F] border border-[#6B8E6F]/20 hover:border-[#6B8E6F]/40 px-3 py-1.5 rounded-lg transition-all", children: "Set up" })] }) })] }), _jsxs("button", { className: `mt-3 text-sm font-light flex items-center gap-1.5 transition-colors ${isDark
                                                        ? 'text-[#fbbf24] hover:text-[#d97706]'
                                                        : 'text-[#d97706] hover:text-[#b45309]'}`, children: [_jsx(Settings, { className: "w-4 h-4" }), _jsx("span", { children: "Create custom automation" })] })] })] }) })), _jsxs("div", { className: "flex-1 overflow-auto", children: [currentView === 'table' && (_jsx("div", { className: "h-full overflow-x-auto", children: _jsx("div", { style: { minWidth: `${pinnedColumnsWidth + 800}px` }, children: renderTableView() }) })), currentView === 'kanban' && (_jsx("div", { className: "h-full", children: _jsx(EnhancedKanbanView, { items: filteredItems, groups: groups, columns: columns, onItemUpdate: (itemId, groupId, updates) => {
                                                updateItemData(itemId, Object.keys(updates)[0], updates[Object.keys(updates)[0]]);
                                            }, onItemCreate: handleAddItem, isDark: isDark, people: people }) })), currentView === 'calendar' && (_jsx("div", { className: "h-full", children: _jsx(CalendarView, { tasks: items.flatMap(item => ({
                                                id: item.id,
                                                name: item.data['col-1'] || 'Untitled',
                                                dueDate: item.data['col-4'] ? new Date(item.data['col-4']) : null,
                                                status: getStatusFromValue(item.data['col-2']) || 'todo',
                                            })), onTaskUpdate: handleTaskUpdate, onTaskCreate: handleTaskCreate, onTaskDelete: handleTaskDelete, projectId: boardData?.boardId || 'board-project', isLoading: false }) })), currentView === 'timeline' && (_jsx("div", { className: "h-full", children: _jsx(TimelineView, { tasks: items.map(item => ({
                                                id: item.id,
                                                name: item.data['col-1'] || 'Untitled',
                                                description: '',
                                                dueDate: item.data['col-4'] ? new Date(item.data['col-4']) : null,
                                                status: getStatusFromValue(item.data['col-2']) || 'todo',
                                                priority: 'medium',
                                                assigneeId: item.data['col-3'] || undefined,
                                                tags: [],
                                                position: 0,
                                                createdAt: new Date(),
                                                updatedAt: new Date(),
                                            })), onTaskUpdate: handleTaskUpdate, onTaskCreate: handleTaskCreate, onTaskDelete: handleTaskDelete, projectId: boardData?.boardId || 'board-project', isLoading: false }) })), currentView === 'app' && (_jsx("div", { className: "h-full", children: _jsx(AppView, { boardId: boardId || boardData?.boardId || 'default-board', workspaceId: boardData?.workspaceId || 'default-workspace' }) })), currentView === 'gantt' && (_jsx("div", { className: "h-full", children: _jsx(GanttView, { tasks: items.map(item => ({
                                                id: item.id,
                                                name: item.data['col-1'] || 'Untitled',
                                                description: '',
                                                startDate: item.data['col-4'] ? new Date(item.data['col-4']) : null,
                                                dueDate: item.data['col-4'] ? new Date(item.data['col-4']) : null,
                                                status: getStatusFromValue(item.data['col-2']) || 'todo',
                                                priority: 'medium',
                                                assigneeId: item.data['col-3'] || undefined,
                                                tags: [],
                                                position: 0,
                                                createdAt: new Date(),
                                                updatedAt: new Date(),
                                            })), onTaskUpdate: handleTaskUpdate, onTaskCreate: handleTaskCreate, onTaskDelete: handleTaskDelete, projectId: boardData?.boardId || 'board-project', isLoading: false }) }))] })] }), _jsx(AddColumnModal, {}), editingStatusColumnId && (_jsx(StatusColumnEditor, { isOpen: showStatusEditor, onClose: () => {
                            setShowStatusEditor(false);
                            setEditingStatusColumnId(null);
                        }, labels: columns.find(col => col.id === editingStatusColumnId)?.options || [], onLabelsChange: handleStatusLabelsChange, columnId: editingStatusColumnId, columnName: columns.find(col => col.id === editingStatusColumnId)?.name || '', onColumnNameChange: handleSaveColumnName, onDeleteColumn: handleDeleteColumn })), editingPersonColumnId && (_jsx(PersonColumnEditor, { isOpen: showPersonEditor, onClose: () => {
                            setShowPersonEditor(false);
                            setEditingPersonColumnId(null);
                        }, people: people, isDark: isDark, columnId: editingPersonColumnId, columnName: columns.find(col => col.id === editingPersonColumnId)?.name || '', onColumnNameChange: handleSaveColumnName, onDeleteColumn: handleDeleteColumn })), editingColumnNameId && (_jsx(ColumnNameEditor, { columnId: editingColumnNameId, currentName: columns.find(c => c.id === editingColumnNameId)?.name || '', isOpen: showColumnNameEditor, onClose: () => {
                            setShowColumnNameEditor(false);
                            setEditingColumnNameId(null);
                        }, onSave: handleSaveColumnName })), _jsx(GroupEditModal, { isOpen: showGroupEditModal, group: editingGroup, onClose: () => {
                            setShowGroupEditModal(false);
                            setEditingGroup(null);
                        }, onSave: handleSaveGroup, isDark: isDark }), _jsx(ColumnSettingsModal, { isOpen: showColumnSettingsModal, column: editingColumnForSettings, onClose: () => {
                            setShowColumnSettingsModal(false);
                            setEditingColumnForSettings(null);
                        }, onSave: handleSaveColumnSettings, onDelete: handleDeleteColumn, isDark: isDark }), _jsx(BoardInfoModal, { isOpen: showBoardInfo, onClose: () => setShowBoardInfo(false), boardId: boardId || '', boardTitle: editTitle, boardDescription: editDescription, boardType: boardType, onBoardTypeChange: handleBoardTypeChange, members: boardMembers, createdBy: {
                            name: currentUser?.name || 'Unknown',
                            avatar: currentUser?.avatar || null
                        }, createdAt: boardInfo?.createdAt ? new Date(boardInfo.createdAt) : new Date(), onOpenInviteModal: () => {
                            setShowBoardInfo(false);
                            setShowInviteModal(true);
                        }, onOpenNotificationsModal: () => {
                            setShowBoardInfo(false);
                            setShowNotificationsModal(true);
                        }, isDark: isDark }), _jsx(BoardInviteModal, { isOpen: showInviteModal, onClose: () => setShowInviteModal(false), boardId: boardId || '', boardTitle: editTitle, members: boardMembers, currentUserId: currentUser?.id || '', onInviteMember: handleInviteMember, onRemoveMember: handleRemoveMember, onUpdateMemberRole: handleUpdateMemberRole, isDark: isDark }), _jsx(BoardNotificationsModal, { isOpen: showNotificationsModal, onClose: () => setShowNotificationsModal(false), boardId: boardId || '', userId: currentUser?.id || '', isDark: isDark }), _jsx(MooPanel, { isOpen: showMooPanel, onClose: () => setShowMooPanel(false), boardTitle: editTitle, userName: currentUser?.name }), _jsx(ActivityLogPanel, { isOpen: showActivityLog, onClose: () => setShowActivityLog(false), boardId: boardId || '', isDark: isDark }), _jsx(BoardImportModal, { isOpen: showImportModal, onClose: () => setShowImportModal(false), boardId: boardId || '', boardItemTypeName: "items", onImportComplete: async (importedCount) => {
                            console.log(`[Board] Import completed with ${importedCount} items, refreshing board...`);
                            setShowImportModal(false);
                            // Refresh board data to show newly imported items/groups
                            await fetchBoardData();
                        } }), selectedItemId && (_jsx(ItemDetailPanel, { itemId: selectedItemId, itemName: items.find(item => item.id === selectedItemId)?.data['col-1'] || 'Untitled Item', isOpen: showDetailPanel, onClose: () => {
                            setShowDetailPanel(false);
                            setSelectedItemId(null);
                        }, updates: [], onAddUpdate: (content) => {
                            console.log('Add update:', content);
                            // TODO: Integrate with Supabase to save updates
                        } })), _jsx(BulkEditModal, { isOpen: showBulkEditModal, onClose: () => setShowBulkEditModal(false), selectedCount: selectedItems.length, columns: columns, onApplyEdit: handleBulkEdit, isDark: isDark })] })] }));
    function renderTableView() {
        return (_jsx("table", { className: "w-full bg-transparent", children: _jsx("tbody", { className: " divide-y divide-white/10", children: _jsx(SortableContext, { items: processedItems.map(item => item.id), strategy: verticalListSortingStrategy, children: dynamicGroups.map((group) => {
                        const groupItems = processedItems
                            .filter(item => item.groupId === group.id)
                            .sort((a, b) => a.position - b.position);
                        const isCollapsed = collapsedGroups.includes(group.id);
                        return (_jsxs(React.Fragment, { children: [_jsx(DroppableGroupHeader, { group: group, itemCount: groupItems.length, isCollapsed: isCollapsed, onToggle: () => toggleGroup(group.id), onAddItem: () => handleAddItem(group.id), onEditGroup: () => handleEditGroup(group), onDeleteGroup: () => handleDeleteGroup(group.id), isDark: isDark }), !isCollapsed && (_jsxs("tr", { className: " border-b border-white/20 sticky bg-transparent", style: { top: '60px', zIndex: 10 }, children: [_jsx("th", { className: "w-12 px-4 py-3", children: (() => {
                                                const groupItemIds = groupItems.map(item => item.id);
                                                const groupAllSelected = groupItemIds.every(id => selectedItems.includes(id)) && groupItems.length > 0;
                                                const groupPartiallySelected = groupItemIds.some(id => selectedItems.includes(id)) && !groupAllSelected;
                                                const handleToggleGroupSelection = () => {
                                                    if (groupAllSelected) {
                                                        // Deselect all items in this group
                                                        setSelectedItems(prev => prev.filter(id => !groupItemIds.includes(id)));
                                                    }
                                                    else {
                                                        // Select all items in this group
                                                        setSelectedItems(prev => {
                                                            const newSet = new Set(prev);
                                                            groupItemIds.forEach(id => newSet.add(id));
                                                            return Array.from(newSet);
                                                        });
                                                    }
                                                };
                                                return (_jsx("button", { onClick: handleToggleGroupSelection, className: `w-4 h-4 border rounded flex items-center justify-center transition-all ${groupAllSelected
                                                        ? 'bg-blue-600 border-blue-600 text-white'
                                                        : groupPartiallySelected
                                                            ? 'bg-blue-600 border-blue-600 text-white'
                                                            : 'border-gray-300 hover:border-blue-500'}`, title: groupAllSelected ? 'Deselect all in group' : 'Select all in group', children: groupAllSelected ? (_jsx(Check, { className: "w-3 h-3" })) : groupPartiallySelected ? (_jsx(Minus, { className: "w-3 h-3" })) : null }));
                                            })() }), _jsx(SortableContext, { items: columns.filter(col => !col.isPinned).map(col => col.id), strategy: horizontalListSortingStrategy, children: columns
                                                .sort((a, b) => a.position - b.position)
                                                .map((column, columnIndex) => (_jsx(DraggableColumnHeader, { column: column, columnIndex: columnIndex, isPinned: pinnedColumns.includes(column.id), onTogglePin: toggleColumnPin, onEditStatusLabels: (columnId) => {
                                                    setEditingStatusColumnId(columnId);
                                                    setShowStatusEditor(true);
                                                }, onEditPersonColumn: (columnId) => {
                                                    setEditingPersonColumnId(columnId);
                                                    setShowPersonEditor(true);
                                                }, onEditNumbers: handleEditNumbers, onEditColumnName: (columnId) => {
                                                    const column = columns.find(c => c.id === columnId);
                                                    if (column) {
                                                        setEditingColumnForSettings(column);
                                                        setShowColumnSettingsModal(true);
                                                    }
                                                }, pinnedOffset: pinnedColumns.includes(column.id) ? getPinnedOffset(columnIndex, columns) : 0, isDark: isDark }, column.id))) }), _jsx("th", { className: "w-12 px-4 py-3", children: _jsx("button", { onClick: () => setShowAddColumnModal(true), className: `p-2 rounded transition-colors ${isDark
                                                    ? 'text-gray-400 hover:text-white hover:bg-gray-700'
                                                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'}`, title: "Add column", children: _jsx(Plus, { className: "w-4 h-4" }) }) })] })), !isCollapsed && (_jsx(_Fragment, { children: groupItems.map((item) => (_jsx(DraggableItem, { item: item, columns: columns, people: people, updateItemData: updateItemData, handleDeleteItem: handleDeleteItem, groupColor: group.color, pinnedColumns: pinnedColumns, getPinnedOffset: getPinnedOffset, isSelected: selectedItems.includes(item.id), onToggleSelection: toggleItemSelection, isDark: isDark, onSelectItemDetail: setSelectedItemId, onOpenDetailPanel: () => setShowDetailPanel(true), onCelebrate: () => setTriggerConfetti(true) }, item.id))) })), !isCollapsed && groupItems.length > 0 && (_jsxs("tr", { className: `border-t-2 font-medium text-sm bg-transparent ${isDark ? 'border-white/20' : 'border-gray-300'}`, children: [_jsx("td", { className: "w-12 px-4 py-2" }), columns
                                            .sort((a, b) => a.position - b.position)
                                            .map((column, columnIndex) => {
                                            const summaries = calculateGroupSummaries(groupItems, [column]);
                                            const summary = summaries[column.id];
                                            return (_jsx("td", { className: isDark ? 'px-4 py-2 text-white' : 'px-4 py-2 text-gray-900', style: {
                                                    width: column.width,
                                                    position: pinnedColumns.includes(column.id) ? 'sticky' : undefined,
                                                    left: pinnedColumns.includes(column.id) ? getPinnedOffset(columnIndex, columns) : undefined,
                                                    zIndex: pinnedColumns.includes(column.id) ? 8 : undefined,
                                                    backgroundColor: pinnedColumns.includes(column.id) ? (isDark ? '#1f2937' : '#f9fafb') : undefined,
                                                }, children: columnIndex === 0 ? (_jsxs("span", { className: `font-semibold ${isDark ? 'text-gray-200' : 'text-gray-700'}`, children: ["\u03A3 ", groupItems.length, " items"] })) : column.type === 'number' && summary?.sum !== undefined ? (_jsxs("div", { className: "flex items-center justify-between", children: [_jsxs("span", { className: `font-semibold ${isDark ? 'text-blue-400' : 'text-blue-600'}`, children: ["\u03A3 ", summary.sum.toFixed(2)] }), _jsxs("div", { className: `text-xs ${isDark ? 'text-gray-300' : 'text-gray-600'}`, children: ["avg: ", summary.average.toFixed(1)] })] })) : summary?.count > 0 ? (_jsxs("span", { className: `text-xs ${isDark ? 'text-gray-300' : 'text-gray-600'}`, children: [summary.count, " filled"] })) : (_jsx("span", { className: isDark ? 'text-gray-400' : 'text-gray-500', children: "\u2014" })) }, column.id));
                                        }), _jsx("td", { className: "w-12 px-4 py-2" })] })), !isCollapsed && (_jsxs("tr", { className: isDark ? 'hover:bg-blue-500/20' : 'hover:bg-blue-50', children: [_jsx("td", { className: "w-12 px-4 py-3" }), _jsx("td", { className: `px-4 py-3 text-sm ${isDark ? 'text-gray-500' : 'text-gray-600'}`, style: {
                                                width: '250px',
                                                position: 'sticky',
                                                left: 0,
                                                backgroundColor: 'transparent',
                                                zIndex: 8,
                                            }, children: _jsxs("button", { onClick: () => handleAddItem(group.id), className: `flex items-center space-x-2 transition-colors ${isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'}`, children: [_jsx(Plus, { className: "w-4 h-4" }), _jsx("span", { children: "Add item" })] }) }), columns.filter(col => col.id !== 'col-1').map(col => (_jsx("td", { style: { width: col.width } }, col.id))), _jsx("td", { className: "w-12 px-4 py-3" })] }))] }, group.id));
                    }) }) }) }));
    }
};
